//
// API service layer — every path, HTTP method, and response shape matches
// the deployed Lambda handlers exactly. See lambda/innovateflow-api/ for
// the handler source code.
import axios from 'axios'
import { STORAGE_KEYS } from '../constants/config'
import { performLogout, getIsLoggingOut } from '@secureembed/shared-crm'
import type {
  InnovateFlowConfig,
  OrgMembership,
  Project,
  ProjectOwner,
  Folder,
  Document,
  Permission,
  EffectivePermissions,
  SearchParams,
  SearchResponse,
  ConnectionsNode,
  ConnectionsEdge,
  PersonalList,
  PersonalItem,
  Review,
  AuditEntry,
  Notification,
  ConnectionRequest,
  DiscoveryResult,
  NoveltyReport,
  UserProfile,
  DisclosureAgreement,
  DisclosureContent,
  FolderListItem,
  DocumentListItem,
  FolderNote,
  FolderConnectionsData,
  SuggestedAction,
  IntakeAnswers,
  IntakeOptions,
  EcosystemProfile,
  EcosystemSearchFilters,
  EcosystemMatch,
  EcosystemConnection,
  LinkedInProfile,
  SkillCategory,
  Rating,
  RatingDimensions,
  Reputation,
  ExpertReview,
  WorkflowInstance,
  WorkflowDefinition,
  AssessmentApiResponse,
  IdeaComponent,
  MatchSummary,
  DirectoryEntity,
  DirectoryCopy,
  DirectorySearchParams,
  DirectoryFacets,
  DirectoryGeoSearch,
  DirectoryMatchResult,
  DirectoryPublication,
  DirectoryCoAuthor,
  ProjectAIAnalysis,
  ProjectClaim,
  ProjectClaimsResult,
  FolderAIAnalysis,
  ClarifyingQuestion,
  PriorArtSummary,
  ReprocessJob,
} from '../types'

declare global {
  interface Window {
    INNOVATEFLOW_CONFIG: InnovateFlowConfig
  }
}

const config = typeof window !== 'undefined' ? window.INNOVATEFLOW_CONFIG : null

const api = axios.create({
  baseURL: config?.API_ENDPOINT ? `${config.API_ENDPOINT}/innovateflow` : '',
  headers: { 'Content-Type': 'application/json' },
})

// Exported (rather than inlined into interceptors.request.use) so it is a
// plain, mock-free-testable function: real localStorage reads, real header
// mutation, no axios internals required to exercise it.
export const applyRequestHeaders = <
  T extends { headers: Record<string, unknown>; params?: Record<string, unknown> },
>(
  reqConfig: T,
): T => {
  const token = localStorage.getItem(STORAGE_KEYS.ID_TOKEN)
  if (token) {
    reqConfig.headers.Authorization = `Bearer ${token}`
  }
  // An aggregator analyst belongs to multiple aggregators; the backend scopes
  // every aggregator request by this header. Injected globally so no call site
  // can omit it — non-aggregator users have no value to send.
  const activeAggregatorId = localStorage.getItem('activeAggregatorId')
  if (activeAggregatorId) {
    reqConfig.headers['X-Aggregator-Id'] = activeAggregatorId
  }
  // Story #5648: own scope is requested by ?scope=own alone, and the server
  // is required to ignore X-Aggregator-Id when it is present. The client
  // must not send what the server is required to ignore — a stale
  // activeAggregatorId in localStorage would otherwise silently ride along
  // on every own-scope call. A per-call `headers: undefined` in
  // scopedRequest does NOT remove a header this interceptor injects
  // globally, so the deletion has to happen here.
  if (reqConfig.params?.scope === 'own') {
    delete reqConfig.headers['X-Aggregator-Id']
  }
  return reqConfig
}

api.interceptors.request.use(applyRequestHeaders)

// ---------------------------------------------------------------------------
// Token refresh (BUG-013 / BUG-014)
//
// Cognito issues short-lived (≈1 h) ID/access tokens plus a long-lived refresh
// token. Before this flow the app had no way to use that refresh token, so the
// FIRST 401 after the ID token expired — the guaranteed, hourly case — logged
// the whole app out. Now a 401 first attempts a silent REFRESH_TOKEN_AUTH and
// retries the original request once; only a genuine refresh failure logs out.
//
// SECURITY / BUG-013 TODO (httpOnly hardening): the refresh token still lives in
// localStorage, so it remains XSS-exfiltratable. The durable fix is to hold the
// refresh token in an httpOnly, SameSite cookie and mint new ID tokens via a
// backend `/auth/refresh` endpoint (shared-crm already ships a
// `createCookieAuthDriver` pilot for exactly this). That requires a backend
// endpoint that is NOT yet deployed for innovateflow-app, so until it exists we
// refresh client-side against the existing Cognito refresh token. Do NOT remove
// this note until the cookie-backed endpoint lands.
// ---------------------------------------------------------------------------

const clearAuthStorage = () =>
  performLogout({
    // BUG-W10-02: include activeAggregatorId so a session-expiry cleanup
    // doesn't leak the previous user's aggregator selection into the next
    // login. Matches the manual-logout key set in AuthContext.
    storageKeys: [STORAGE_KEYS.ID_TOKEN, STORAGE_KEYS.ACCESS_TOKEN, STORAGE_KEYS.REFRESH_TOKEN, 'activeAggregatorId'],
  })

/**
 * Exchange the stored Cognito refresh token for a fresh ID/access token via
 * REFRESH_TOKEN_AUTH. Persists the new tokens to localStorage and resolves with
 * the new ID token, or `null` when no refresh token is stored / the exchange
 * yields no AuthenticationResult. Throws only on a transport error.
 *
 * NB: REFRESH_TOKEN_AUTH does NOT return a new refresh token, so the existing
 * one is preserved untouched.
 */
export async function refreshTokens(): Promise<string | null> {
  const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN)
  if (!refreshToken || !config?.COGNITO_CLIENT_ID || !config?.REGION) return null

  const cognitoUrl = `https://cognito-idp.${config.REGION}.amazonaws.com/`
  const response = await fetch(cognitoUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth',
    },
    body: JSON.stringify({
      AuthFlow: 'REFRESH_TOKEN_AUTH',
      ClientId: config.COGNITO_CLIENT_ID,
      AuthParameters: { REFRESH_TOKEN: refreshToken },
    }),
  })
  const data = await response.json()
  const result = data?.AuthenticationResult
  if (result?.IdToken) {
    localStorage.setItem(STORAGE_KEYS.ID_TOKEN, result.IdToken)
    if (result.AccessToken) localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, result.AccessToken)
    return result.IdToken as string
  }
  return null
}

// Dedupe concurrent refreshes: many in-flight requests can 401 at once, but we
// only ever want a single REFRESH_TOKEN_AUTH round-trip. All callers await the
// same promise; it self-clears once settled so the NEXT expiry can refresh again.
let refreshInFlight: Promise<string | null> | null = null
function refreshTokensDeduped(): Promise<string | null> {
  if (!refreshInFlight) {
    refreshInFlight = refreshTokens().finally(() => { refreshInFlight = null })
  }
  return refreshInFlight
}

/** Story #6490 8.3 — dispatched on a 403 {error: 'key_revoked'} from the key-status gate. */
export const KEY_REVOKED_EVENT = 'innovateflow:key-revoked'
export const KEY_REVOKED_TEXT =
  'Search and analysis are paused for this workspace because its encryption key was revoked or is unavailable.'

/** Raise the app-wide banner for a key-status 403. Returns true when it was one —
 *  for fetch-based callers (useAuthedFetch, the key pages) as well as axios. */
export function notifyIfKeyRevoked(status: number, body: unknown): boolean {
  const b = body as { error?: unknown; tenant?: unknown; reason?: unknown } | null
  if (status !== 403 || !b || b.error !== 'key_revoked' || typeof window === 'undefined') return false
  window.dispatchEvent(new CustomEvent(KEY_REVOKED_EVENT, {
    detail: { tenant: typeof b.tenant === 'string' ? b.tenant : null,
              reason: typeof b.reason === 'string' ? b.reason : 'key_revoked' },
  }))
  return true
}

interface RetriableRequestConfig {
  _retried?: boolean
  headers?: Record<string, unknown>
  [key: string]: unknown
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status
    const original = error.config as RetriableRequestConfig | undefined

    // Reactive refresh: on the first 401 for a request, try to mint a fresh ID
    // token from the refresh token and replay the request once. Guard against
    // loops with `_retried`, and skip entirely mid-logout or with no refresh
    // token to trade against.
    if (
      status === 401 &&
      !getIsLoggingOut() &&
      original &&
      !original._retried &&
      localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN)
    ) {
      original._retried = true
      try {
        const newIdToken = await refreshTokensDeduped()
        if (newIdToken) {
          original.headers = { ...(original.headers ?? {}), Authorization: `Bearer ${newIdToken}` }
          return api(original as never)
        }
      } catch {
        // fall through to logout below
      }
      // Refresh failed (no new token / transport error) → the session is truly
      // dead. Clear auth and reject.
      clearAuthStorage()
      return Promise.reject(error)
    }

    // Any other 401 (already retried, no refresh token, or mid-logout) → logout.
    if (status === 401 && !getIsLoggingOut()) {
      clearAuthStorage()
    }
    // Story #6490 8.3: the key-status gate's 403 is not a generic error — the
    // app-wide KeyRevokedBanner names the paused workspace.
    if (status) notifyIfKeyRevoked(status, error.response?.data)
    return Promise.reject(error)
  }
)

// ---------------------------------------------------------------------------
// Projects — projects/handler.py
// ---------------------------------------------------------------------------

// GET /innovateflow/projects → list_projects (path_parts len 2)
// Returns: { projects: [...], count: N }
export const getProjects = () =>
  api.get<{ projects: Project[]; count: number }>('/projects').then((r) => r.data.projects)

// GET /innovateflow/projects/{id} → get_project_by_id (path_parts len 3)
// Resolves a single project the caller can view — including cross-org collaborators
// granted access via full disclosure, who do NOT have it in their own getProjects() list.
export const getProject = (id: string) =>
  api.get<{ project: Project }>(`/projects/${id}`).then((r) => r.data.project)

// POST /innovateflow/projects → create_project (path_parts len 2)
// Returns: { project: {...} }
export const createProject = (data: Partial<Project>) =>
  api.post<{ project: Project }>('/projects', data).then((r) => r.data.project)

// PUT /innovateflow/projects/{id} → update_project (path_parts len 3)
// Returns: { project: {...} }
export const updateProject = (id: string, data: Partial<Project>) =>
  api.put<{ project: Project }>(`/projects/${id}`, data).then((r) => r.data.project)

// DELETE /innovateflow/projects/{id} → delete_project (path_parts len 3)
// Returns: { message: 'Project archived' }
export const deleteProject = (id: string) => api.delete(`/projects/${id}`)

export interface ProjectRecoveryResult {
  operation_id: string
  status: 'EXACT_PARENT_RECONSTRUCTED' | 'PROJECT_DEPENDENCIES_RECONSTRUCTED' | 'OWNER_CONFIRMATION_REQUIRED' | 'PREPARING' | 'PREPARED' | 'BATCH_APPLIED' | 'UNKNOWN_OR_PENDING_NO_REPLAY' | 'UNKNOWN_OR_CONFLICT_NO_REPLAY'
  application_qualified: false
  replayed: false
  cursor?: string
  batch_index?: number
  rows_prepared?: number
  business_rows_written?: number
}

export const confirmProjectRecovery = async (projectId: string, operationId: string, idempotencyKey: string, cursor?: string): Promise<ProjectRecoveryResult> => {
  const response = await api.post<ProjectRecoveryResult>(`/projects/${encodeURIComponent(projectId)}/recover`, {
    operation_id: operationId, idempotency_key: idempotencyKey, ...(cursor ? { cursor } : {}),
  })
  const result = response.data
  if (result.operation_id !== operationId || result.application_qualified !== false || result.replayed !== false ||
    !['EXACT_PARENT_RECONSTRUCTED', 'PROJECT_DEPENDENCIES_RECONSTRUCTED', 'OWNER_CONFIRMATION_REQUIRED', 'PREPARING', 'PREPARED', 'BATCH_APPLIED', 'UNKNOWN_OR_PENDING_NO_REPLAY', 'UNKNOWN_OR_CONFLICT_NO_REPLAY'].includes(result.status) ||
    (result.cursor !== undefined && !/^[a-f0-9]{64}$/.test(result.cursor))) {
    throw new Error('The recovery response could not be matched to this operation. Check the existing request before continuing.')
  }
  return result
}

// GET /innovateflow/projects/{id}/owners → list_owners (path_parts len 4)
// Returns: { owners: [{user_id, email}, ...] }
export const getProjectOwners = (projectId: string) =>
  api.get<{ owners: ProjectOwner[] }>(`/projects/${projectId}/owners`).then((r) => r.data.owners)

// POST /innovateflow/projects/{id}/owners → add_owner (path_parts len 4)
// Body: { email }
// Returns: { owners: [{user_id, email}, ...] }
export const addProjectOwner = (projectId: string, email: string) =>
  api.post<{ owners: ProjectOwner[] }>(`/projects/${projectId}/owners`, { email }).then((r) => r.data.owners)

// DELETE /innovateflow/projects/{id}/owners/{user_id} → remove_owner (path_parts len 5)
// Returns: { owners: [{user_id, email}, ...] }
export const removeProjectOwner = (projectId: string, userId: string) =>
  api.delete<{ owners: ProjectOwner[] }>(`/projects/${projectId}/owners/${userId}`).then((r) => r.data.owners)

// POST /innovateflow/projects/{id}/approve-review → approve_review (path_parts len 4)
// Returns: { review_status: 'approved', review_approved_at: string }
export const approveProjectReview = (projectId: string) =>
  api.post<{ review_status: string; review_approved_at: string }>(`/projects/${projectId}/approve-review`).then((r) => r.data)

// ---------------------------------------------------------------------------
// Folders — folders/handler.py (FLAT paths, NOT nested under /projects)
// ---------------------------------------------------------------------------

// GET /innovateflow/folders?project_id=X → list_folders (path_parts len 2)
// Returns: { folders: [...] }
export const getFolders = (projectId: string) =>
  api.get<{ folders: Folder[] }>('/folders', { params: { project_id: projectId } }).then((r) => r.data.folders)

// POST /innovateflow/folders → create_folder (path_parts len 2, project_id in body)
// Returns: { folder: {...} }
export const createFolder = (projectId: string, data: Partial<Folder>) =>
  api.post<{ folder: Folder }>('/folders', { ...data, project_id: projectId }).then((r) => r.data.folder)

// PUT /innovateflow/folders/{project_id}/{folder_id} → rename_folder (path_parts len 4)
// Returns: { folder: {...} }
export const updateFolder = (projectId: string, folderId: string, data: Partial<Folder>) =>
  api.put<{ folder: Folder }>(`/folders/${projectId}/${folderId}`, data).then((r) => r.data.folder)

// DELETE /innovateflow/folders/{project_id}/{folder_id} → delete_folder (path_parts len 4)
// Returns: { message: 'Folder deleted' }
export const deleteFolder = (projectId: string, folderId: string) =>
  api.delete(`/folders/${projectId}/${folderId}`)

// ---------------------------------------------------------------------------
// Documents — documents/handler.py (FLAT paths)
// ---------------------------------------------------------------------------

// GET /innovateflow/documents?project_id=X&folder_id=Y → list_documents (path_parts len 2)
// Returns: { documents: [...], count: N }
//
// BUG-3221-FIX-S2D: callers that need to render the "Missing file" badge +
// hide/show toggle (inventor's own project/folder views) pass
// `includeOrphaned=true` to receive s3_orphaned docs alongside healthy ones.
// Defaults to false so unrelated callers (collaborator view, reference lists,
// journals panel) keep the Stream 2C defense-in-depth — they never see orphans.
export const getDocuments = (projectId: string, folderId?: string, includeOrphaned = false) => {
  const params: Record<string, string> = { project_id: projectId }
  if (folderId) params.folder_id = folderId
  if (includeOrphaned) params.include_orphaned = 'true'
  return api.get<{ documents: Document[]; count: number }>('/documents', { params }).then((r) => r.data.documents)
}

// GET /innovateflow/documents/{id} → get_document (path_parts len 3)
// Returns: { document: {...} }
export const getDocument = (documentId: string) =>
  api.get<{ document: Document }>(`/documents/${documentId}`).then((r) => r.data.document)

// GET /innovateflow/documents/{id}/download → download_document (path_parts len 4, path_parts[3]=='download')
// Returns: { download_url: '...', expires_in: 3600 }
export const downloadDocument = (documentId: string) =>
  api.get<{ download_url: string; expires_in: number }>(`/documents/${documentId}/download`).then((r) => r.data.download_url)

// BUG-145: GET /innovateflow/documents/{id}/render/grid → render_grid.
// A bare <img src> can't send the Bearer token AND the endpoint returns JSON
// (a presigned S3 URL), not image bytes. Fetch the presigned URL over the
// authed axios, then use it as the <img src> (S3 presign needs no auth header).
// 404 = no rendered views for this document.
export const getDocumentRenderGrid = (documentId: string) =>
  api.get<{ render_url: string; expires_in: number }>(`/documents/${documentId}/render/grid`).then((r) => r.data.render_url)

// PUT /innovateflow/documents/{id} → update_document (path_parts len 3)
// Returns: { document: {...} }
export const updateDocument = (documentId: string, data: Partial<Document>) =>
  api.put<{ document: Document }>(`/documents/${documentId}`, data).then((r) => r.data.document)

// DELETE /innovateflow/documents/{id} → delete_document (path_parts len 3)
// Returns: { message: 'Document deleted' }
export const deleteDocument = (documentId: string) => api.delete(`/documents/${documentId}`)

// POST /innovateflow/documents/{id}/retry-embedding → retry_embedding (path_parts len 4, path_parts[3]=='retry-embedding')
// Returns: { status: 'success' | 'error', result: {...} }
export const retryEmbedding = (documentId: string) =>
  api.post<{ status: string; result: Record<string, unknown> }>(`/documents/${documentId}/retry-embedding`).then((r) => r.data)

// Move a document to a different folder (or root)
export const moveDocument = (documentId: string, folderId: string) =>
  updateDocument(documentId, { folder_id: folderId } as Partial<Document>)

// ---------------------------------------------------------------------------
// Upload — upload/handler.py (FLAT /upload path, NOT nested under /projects)
// ---------------------------------------------------------------------------

// POST /innovateflow/upload → generate_upload_url (path_parts len 2)
// Body: { project_id, filename, content_type }
// Returns: { upload_url, document_id, s3_key, expires_in, required_headers }
export const getUploadUrl = (projectId: string, fileName: string, contentType: string, folderId?: string) =>
  api.post<{ upload_url: string; document_id: string; s3_key: string; upload_id?: string; expires_in: number; required_headers?: Record<string, string> }>('/upload', {
    project_id: projectId,
    filename: fileName,
    content_type: contentType,
    folder_id: folderId,
  }).then((r) => r.data)

// POST /innovateflow/upload/confirm → confirm_upload (path_parts len 3, path_parts[2]=='confirm')
// Body: { document_id, project_id, s3_key, title, folder_id?, description?, tags?, filename?, content_type?, document_type? }
// Returns: { document: {...} }
// document_type: the user-confirmed document type from the guided-submission
// wizard (BUG-092). Sent so the backend can honour the user's correction instead
// of always auto-classifying from header bytes.
export interface ConfirmUploadData {
  document_id: string
  upload_id?: string
  project_id: string
  s3_key: string
  title: string
  folder_id?: string
  description?: string
  tags?: string[]
  filename?: string
  content_type?: string
  document_type?: string
}
export const confirmUpload = (data: ConfirmUploadData) =>
  api.post<{ document: Document }>('/upload/confirm', data).then((r) => r.data.document)

// POST /innovateflow/upload/promote — Stream 7 (#3559): org admin promotes
// a quarantined collaborator document to the uploads/ prefix and triggers embedding.
export const promoteDocument = (documentId: string) =>
  api.post<{ document_id: string; s3_key: string; embed_status: string }>('/upload/promote', { document_id: documentId }).then((r) => r.data)

// ---------------------------------------------------------------------------
// Permissions — permissions/handler.py (query param for GET, NOT path segment)
// ---------------------------------------------------------------------------

// GET /innovateflow/permissions?resource_id=X → list_permissions (path_parts len 2)
// Returns: { permissions: [...] }
export const getPermissions = (resourceId: string) =>
  api.get<{ permissions: Permission[] }>('/permissions', { params: { resource_id: resourceId } }).then((r) => r.data.permissions)

// POST /innovateflow/permissions → grant_permission (path_parts len 2)
// Body: { resource_id, grantee_email, permission_level, granular fields }
// Returns: { permission: {...} }
export const grantPermission = (data: {
  resource_id: string; grantee_email: string; permission_level: string;
  allow_download?: boolean; allow_print?: boolean; resource_type?: string;
  can_create?: boolean; can_read?: boolean; can_edit?: boolean;
  can_delete?: boolean; can_manage_collaborators?: boolean;
}) =>
  api.post<{ permission: Permission }>('/permissions', data).then((r) => r.data.permission)

// PUT /innovateflow/permissions/{resource_id}/{grantee_id} → update_permission (path_parts len 4)
// Body: { permission_level, granular fields }
// Returns: { permission: {...} }
export const updatePermission = (resourceId: string, granteeId: string, data: {
  permission_level: string;
  can_create?: boolean; can_read?: boolean; can_edit?: boolean;
  can_delete?: boolean; can_manage_collaborators?: boolean;
}) =>
  api.put<{ permission: Permission }>(`/permissions/${resourceId}/${granteeId}`, data).then((r) => r.data.permission)

// GET /innovateflow/permissions/effective?resource_id=X&grantee_id=Y
// Returns: { effective_permissions: {...} }
export const getEffectivePermissions = (resourceId: string, granteeId: string) =>
  api.get<{ effective_permissions: EffectivePermissions }>('/permissions/effective', {
    params: { resource_id: resourceId, grantee_id: granteeId }
  }).then((r) => r.data.effective_permissions)

// Check if current user has manager-level access on a project
export const checkManagerAccess = async (projectId: string, userId: string): Promise<boolean> => {
  try {
    const perms = await getEffectivePermissions(projectId, userId)
    return perms.can_manage_collaborators === true ||
      (perms.permission_level !== null && ['manager', 'admin'].includes(perms.permission_level)) ||
      perms.source === 'owner'
  } catch {
    return false
  }
}

// DELETE /innovateflow/permissions/{resource_id}/{grantee_id} → revoke_permission (path_parts len 4)
// Returns: { message: 'Permission revoked' }
export const revokePermission = (resourceId: string, granteeId: string) =>
  api.delete(`/permissions/${resourceId}/${granteeId}`)

// --- Resource access model (Plan §4.6-4.11) --------------------------------

// POST /innovateflow/permissions — grant/deny for a user, group or external org.
export const grantAccess = (data: {
  resource_id: string
  permission_level: string
  grantee_type?: 'user' | 'group' | 'external_org'
  effect?: 'allow' | 'deny'
  grantee_email?: string
  group_id?: string
  external_org_id?: string
  resource_type?: string
  allow_download?: boolean
  allow_print?: boolean
  expires_at?: string
}) => api.post<{ permission: Permission }>('/permissions', data).then((r) => r.data.permission)

export interface ExplainTrace {
  resource_id: string; member_id: string; can_view: boolean; can_edit: boolean
  effective_level: string | null; source: string; capped_by: string | null
  reason: string; node: string | null
}
// GET /innovateflow/permissions/explain?resource_id=&member_id= — explain-access trace.
export const explainAccess = (resourceId: string, memberId?: string) =>
  api.get<{ explain: ExplainTrace }>('/permissions/explain', {
    params: { resource_id: resourceId, ...(memberId ? { member_id: memberId } : {}) },
  }).then((r) => r.data.explain)

// GET /innovateflow/permissions/accessible?member_id= — materialised accessible set.
export const getAccessibleProjects = (memberId?: string) =>
  api.get<{ member_id: string; accessible_project_ids: string[]; projects?: { project_id: string; name: string }[] }>('/permissions/accessible', {
    params: memberId ? { member_id: memberId } : {},
    // The endpoint now returns `projects` (id + resolved name) so the access
    // matrix/picker can label by name instead of raw UUID; fall back to
    // id-as-name for older backends that only send accessible_project_ids.
  }).then((r) => r.data.projects ?? r.data.accessible_project_ids.map((project_id) => ({ project_id, name: project_id })))

export interface MatrixCell { level: string | null; source: string; allowed: boolean; capped_by?: string | null }
export interface MatrixRow {
  type: 'member' | 'group'
  member_id?: string; email?: string; role?: string; role_display?: string; licence_class?: string
  group_id?: string; name?: string; member_count?: number
  cells: Record<string, MatrixCell>
}
// GET /innovateflow/permissions/matrix?org_id=&scope_ids= — member/group x resource grid.
export const getAccessMatrix = (orgId: string, scopeIds: string[]) =>
  api.get<{ matrix: { org_id: string; scope_ids: string[]; rows: MatrixRow[] } }>('/permissions/matrix', {
    params: { org_id: orgId, scope_ids: scopeIds.join(',') },
  }).then((r) => r.data.matrix)

// ---------------------------------------------------------------------------
// Licensing & seats — licensing/handler.py (Plan §4.1-4.5)
// ---------------------------------------------------------------------------

export interface LicencePool {
  org_id: string
  seats_purchased_full: number; seats_allocated_full: number
  seats_purchased_contributor: number; seats_allocated_contributor: number
  viewer_count: number; viewer_cap?: number
  plan_tier: string | null; stripe_subscription_id?: string | null; updated_at?: string
}
export interface Seat {
  org_id: string; seat_id: string; member_id: string; member_email?: string
  licence_class: 'full' | 'contributor' | 'viewer'; role?: string; role_display?: string
  state: 'invited' | 'active' | 'suspended' | 'removed'; allocated_at?: string
}
export interface RolePackEntry {
  display: string; platform_role: string
  min_licence: 'full' | 'contributor' | 'viewer'; scoped?: boolean; audit?: boolean
}

export const getLicencePool = (orgId: string) =>
  api.get<{ pool: LicencePool }>(`/licensing/pool/${orgId}`).then((r) => r.data.pool)

export const setLicencePool = (orgId: string, data: {
  seats_purchased_full?: number; seats_purchased_contributor?: number
  plan_tier?: string; stripe_subscription_id?: string
}) => api.post<{ pool: LicencePool }>(`/licensing/pool/${orgId}`, data).then((r) => r.data.pool)

export const getSeats = (orgId: string) =>
  api.get<{ seats: Seat[] }>(`/licensing/seats/${orgId}`).then((r) => r.data.seats)

export const allocateSeat = (data: {
  org_id: string; member_id: string; member_email?: string
  licence_class: 'full' | 'contributor' | 'viewer'; role?: string; org_type?: string
}) => api.post<{ seat: Seat }>('/licensing/seats', data).then((r) => r.data.seat)

export const reclaimSeat = (orgId: string, seatId: string) =>
  api.delete(`/licensing/seats/${orgId}/${seatId}`)

export const getRolePack = (orgType: string) =>
  api.get<{ org_type: string; roles: RolePackEntry[] }>(`/licensing/role-packs/${orgType}`).then((r) => r.data.roles)

// ---------------------------------------------------------------------------
// Identity & SSO (federation config) — identity/handler.py (Plan §5)
// ---------------------------------------------------------------------------

export interface IdentityConfig {
  org_id: string
  idp_type: 'saml' | 'oidc' | null
  status: string
  require_sso: boolean
  jit_licensing: boolean
  idle_seat_reclaim: boolean
  verified_domains: string[]
  pending_domains?: Record<string, string>
  group_mappings: { group: string; role: string; resource_group?: string }[]
  break_glass_admins: string[]
}

export const getIdentityConfig = (orgId: string) =>
  api.get<{ config: IdentityConfig }>(`/identity/${orgId}`).then((r) => r.data.config)
export const setIdentityConfig = (orgId: string, data: {
  idp_type?: 'saml' | 'oidc'; metadata_url?: string; metadata_xml?: string
  jit_licensing?: boolean; idle_seat_reclaim?: boolean
}) => api.put<{ config: IdentityConfig }>(`/identity/${orgId}`, data).then((r) => r.data.config)
export const startDomainVerification = (orgId: string, domain: string) =>
  api.post<{ txt_name: string; txt_value: string; domain: string }>(`/identity/${orgId}/domain`, { domain }).then((r) => r.data)
export const verifyDomain = (orgId: string, domain: string) =>
  api.post<{ status: string; reason?: string }>(`/identity/${orgId}/domain/verify`, { domain }).then((r) => r.data)
export const setRequireSso = (orgId: string, enabled: boolean) =>
  api.post(`/identity/${orgId}/require-sso`, { enabled }).then((r) => r.data)
export const addBreakGlassAdmin = (orgId: string, identity: string) =>
  api.post(`/identity/${orgId}/break-glass`, { user_id: identity, email: identity }).then((r) => r.data)
export const issueScimToken = (orgId: string) =>
  api.post<{ scim_token: string; warning: string; rotated: boolean }>(`/identity/${orgId}/scim-token`, {}).then((r) => r.data)
export const setGroupMappings = (orgId: string, mappings: { group: string; role: string; resource_group?: string }[]) =>
  api.post(`/identity/${orgId}/group-mapping`, { mappings }).then((r) => r.data)

// ---------------------------------------------------------------------------
// Encryption Keys (BYOK config) — org_keys/handler.py (Plan §7)
// ---------------------------------------------------------------------------

export interface KeyConfig {
  org_id: string; custody_mode: 'platform' | 'byok'; customer_kms_arn: string | null
  status: string; health?: unknown; rotation: string
  // Story #6490: the organisation's deletion record (org-key-config row).
  purge_state?: string | null; purge_started_at?: string | null; purged_at?: string | null
  purge_alert_sent_at?: string | null
}
export interface KeyValidation {
  valid: boolean; reason?: string; key_state?: string | null; region?: string
  rotation_enabled?: boolean | null; checked_at?: string; detail?: string
}

export const getKeyConfig = (orgId: string) =>
  api.get<{ config: KeyConfig }>(`/org-keys/${orgId}`).then((r) => r.data.config)
export const setKeyConfig = (orgId: string, data: { custody_mode: 'platform' | 'byok'; customer_kms_arn?: string }) =>
  api.put<{ config: KeyConfig }>(`/org-keys/${orgId}`, data).then((r) => r.data.config)
export const validateCustomerKey = (orgId: string, customerKmsArn: string) =>
  api.post<KeyValidation>(`/org-keys/${orgId}/validate`, { customer_kms_arn: customerKmsArn }).then((r) => r.data)
export const getKeyHealth = (orgId: string) =>
  // The /health endpoint returns { status: 'healthy' } for platform-managed orgs
  // (no `valid`/`healthy`/`key_state`); declare `status` so the UI can read it.
  api.get<KeyValidation & { healthy?: boolean; status?: string }>(`/org-keys/${orgId}/health`).then((r) => r.data)
export const getKeyPolicyTemplate = (orgId: string) =>
  api.get<{ statement: unknown; cloudformation: string; required_region: string; encryption_context: unknown }>(`/org-keys/${orgId}/policy`).then((r) => r.data)
export const rewrapDeks = (orgId: string) =>
  api.post(`/org-keys/${orgId}/rewrap`, {}).then((r) => r.data)
export const offboardKeys = (orgId: string, webauthnToken?: string) =>
  api.post(`/org-keys/${orgId}/offboard`, { confirm: true, ...(webauthnToken ? { webauthn_token: webauthnToken } : {}) }).then((r) => r.data)
// Story #6490 3.1 — revoke the platform's access to the org's BYOK key AND delete
// the organisation's search entries / derived vectors (vector_purge in the reply).
export const revokeOrgKeys = (orgId: string, webauthnToken?: string) =>
  api.post(`/org-keys/${orgId}/revoke`, { confirm: true, ...(webauthnToken ? { webauthn_token: webauthnToken } : {}) }).then((r) => r.data)

// ---------------------------------------------------------------------------
// User Preferences (4-layer) — preferences/handler.py (Plan §6)
// ---------------------------------------------------------------------------

export interface ResolvedPreferences {
  preferences: Record<string, unknown>
  sources: Record<string, string>
  locked_keys: string[]
  org_id?: string
  etag?: string
}

export const getMyPreferences = () =>
  api.get<ResolvedPreferences>('/preferences').then((r) => r.data)
export const setMyPreference = (key: string, value: unknown) =>
  api.put<ResolvedPreferences>('/preferences', { key, value }).then((r) => r.data)
export const getOrgPreferencePolicy = (orgId: string) =>
  api.get<{ org_id: string; org_prefs: Record<string, unknown>; locked_keys: string[]; manageable_keys: string[] }>(`/preferences/org/${orgId}`).then((r) => r.data)
export const setOrgPreferencePolicy = (orgId: string, data: { org_prefs?: Record<string, unknown>; locked_keys?: string[] }) =>
  api.put(`/preferences/org/${orgId}`, data).then((r) => r.data)

// ---------------------------------------------------------------------------
// Search — search/handler.py
// ---------------------------------------------------------------------------

// POST /innovateflow/search → search (path_parts len 2)
// Body: { query, scope, embeddings_mode, size }
// Returns: { results: [...], total, query, scope }
export const searchResources = (params: SearchParams) =>
  api.post<SearchResponse>('/search', params).then((r) => r.data)

// ---------------------------------------------------------------------------
// Connections — connections/handler.py (POST with body, NOT GET with path)
// ---------------------------------------------------------------------------

// POST /innovateflow/connections/explore → explore (path_parts len 3, path_parts[2]=='explore')
// Body: { resource_id, project_id? }  BUG-NEW-J: pass project_id for namespace isolation
// Returns: { nodes: [...], edges: [...], center: '...' }
export const exploreConnections = (resourceId: string, projectId?: string) => {
  const body: Record<string, string> = { resource_id: resourceId }
  if (projectId) body.project_id = projectId
  return api.post<{ nodes: ConnectionsNode[]; edges: ConnectionsEdge[]; center: string }>('/connections/explore', body).then((r) => r.data)
}

// POST /innovateflow/connections/discover → discover (path_parts len 3, path_parts[2]=='discover')
// Body: { node_id, node_type, project_id? }  BUG-NEW-J: pass project_id for namespace isolation
// Returns: { nodes: [...], edges: [...], expanded_from: '...' }
export const discoverMore = (nodeId: string, nodeType: string = 'invention', projectId?: string) => {
  const body: Record<string, string> = { node_id: nodeId, node_type: nodeType }
  if (projectId) body.project_id = projectId
  return api.post<{ nodes: ConnectionsNode[]; edges: ConnectionsEdge[]; expanded_from: string }>('/connections/discover', body).then((r) => r.data)
}

// ---------------------------------------------------------------------------
// Lists — resource_lists/handler.py (route key: 'lists')
// ---------------------------------------------------------------------------

// GET /innovateflow/lists → list_lists (path_parts len 2)
// Returns: { lists: [...] }
export const getPersonalLists = () =>
  api.get<{ lists: PersonalList[] }>('/lists').then((r) => r.data.lists)

// POST /innovateflow/lists → create_list (path_parts len 2)
// Body: { name, description? }
// Returns: { list: {...} }
export const createList = (data: Partial<PersonalList>) =>
  api.post<{ list: PersonalList }>('/lists', data).then((r) => r.data.list)

// DELETE /innovateflow/lists/{list_id} → delete_list (path_parts len 3)
// Returns: { message: 'List deleted' }
export const deleteList = (listId: string) => api.delete(`/lists/${listId}`)

// GET /innovateflow/lists/{list_id}/items → list_items (path_parts len 4, path_parts[3]=='items')
// Returns: { items: [...] }
export const getListItems = (listId: string) =>
  api.get<{ items: PersonalItem[] }>(`/lists/${listId}/items`).then((r) => r.data.items)

// POST /innovateflow/lists/{list_id}/items → add_item (path_parts len 4, path_parts[3]=='items')
// Body: { resource_id, resource_type?, title?, notes? }
// Returns: { item: {...} }
export const addListItem = (listId: string, data: Partial<PersonalItem>) =>
  api.post<{ item: PersonalItem }>(`/lists/${listId}/items`, data).then((r) => r.data.item)

// DELETE /innovateflow/lists/{list_id}/items/{resource_id} → remove_item (path_parts len 5, path_parts[3]=='items')
// Returns: { message: 'Item removed' }
export const removeListItem = (listId: string, resourceId: string) =>
  api.delete(`/lists/${listId}/items/${resourceId}`)

// ---------------------------------------------------------------------------
// Reviews — reviews/handler.py
// ---------------------------------------------------------------------------

// GET /innovateflow/reviews → list_reviews (path_parts len 2)
// Returns ALL reviews (sent + received) with 'direction' field.
// Client-side filter by direction.
// Returns: { reviews: [...], count: N }
export const getReviews = (type?: 'sent' | 'received') =>
  api.get<{ reviews: Review[]; count: number }>('/reviews').then((r) => {
    const reviews = r.data.reviews
    if (type) return reviews.filter((rev: Review & { direction?: string }) => rev.direction === type)
    return reviews
  })

// POST /innovateflow/reviews → create_review (path_parts len 2)
// Body: { document_id, reviewers: [...], review_type, message? }
// Returns: { reviews: [...] }
export const createReview = (data: { document_id: string; reviewers: string[]; review_type: string; message?: string }) =>
  api.post<{ reviews: Review[] }>('/reviews', data).then((r) => r.data.reviews)

// PUT /innovateflow/reviews/{review_id} → submit_decision (path_parts len 3)
// Body: { decision: 'approved'|'rejected'|'changes_requested', feedback? }
// Returns: { review: {...} }
export const submitReviewDecision = (reviewId: string, data: { decision: string; feedback?: string }) =>
  api.put<{ review: Review }>(`/reviews/${reviewId}`, data).then((r) => r.data.review)

// DELETE /innovateflow/reviews/{review_id} → cancel_review (path_parts len 3)
// Returns: { message: 'Review cancelled' }
export const cancelReview = (reviewId: string) => api.delete(`/reviews/${reviewId}`)

// ---------------------------------------------------------------------------
// Audit — audit/handler.py (admin-only)
// ---------------------------------------------------------------------------

// GET /innovateflow/audit → query_audit (path_parts len 2)
// Query params: user_id, resource_id, project_id, action_type, from, to, limit
// BUG-NEW-R: project_id expands to all doc-level resource_ids in the project
// so upload/correction/embedding events appear in the project activity timeline.
// Returns: { events: [...], count: N, filters: {...} }
export const getAuditLog = (params?: { user_id?: string; action_type?: string; resource_id?: string; project_id?: string; from?: string; to?: string; limit?: number }) =>
  api.get<{ events: AuditEntry[]; count: number; filters: Record<string, string | null> }>('/audit', { params }).then((r) => r.data)

// ---------------------------------------------------------------------------
// Notifications — notifications/handler.py
// ---------------------------------------------------------------------------

// GET /innovateflow/notifications → list_notifications (path_parts len 2)
// Returns: { notifications: [...], count: N, next_key?: '...' }
export const getNotifications = () =>
  api.get<{ notifications: Notification[]; count: number }>('/notifications').then((r) => r.data.notifications)

// PUT /innovateflow/notifications/{notification_id} → mark_as_read (path_parts len 3)
// NO trailing /read — handler matches on path_parts len 3
// Returns: { message: 'Notification marked as read' }
export const markNotificationRead = (notificationId: string) =>
  api.put(`/notifications/${notificationId}`)

// ---------------------------------------------------------------------------
// Admin — admin/handler.py (admin-only, cannot access document content)
// ---------------------------------------------------------------------------

// GET /innovateflow/admin/stats → get_stats (path_parts[2]=='stats')
// Returns: { stats: { user_count, project_count, document_count, storage_bytes, storage_mb } }
export const getAdminStats = () =>
  api.get<{ stats: { user_count: number; project_count: number; document_count: number; storage_bytes: number; storage_mb: number } }>('/admin/stats').then((r) => r.data.stats)

// GET /innovateflow/admin/users → list_users (path_parts[2]=='users')
// Returns: { users: [...], count, pagination_token? }
export const getAdminUsers = (paginationToken?: string) =>
  api.get<{ users: { user_id: string; email: string; role: string; status: string; enabled: boolean; created: string }[]; count: number; pagination_token?: string }>(
    '/admin/users',
    { params: paginationToken ? { pagination_token: paginationToken } : undefined }
  ).then((r) => r.data)

// POST /innovateflow/admin/users → create_user (path_parts[2]=='users')
// Body: { email, role }
// Returns: { user: { user_id, email, status, role } }
export const createAdminUser = (data: { email: string; role: string }) =>
  api.post<{ user: { user_id: string; email: string; status: string; role: string } }>('/admin/users', data).then((r) => r.data.user)

// PUT /innovateflow/admin/users/{user_id} → update_user (path_parts[2]=='users', path_parts[3]==user_id)
// Body: { role?, enabled? }
// Returns: { message: 'User updated' }
export const updateAdminUser = (userId: string, data: { role?: string; enabled?: boolean }) =>
  api.put(`/admin/users/${userId}`, data)

// GET /innovateflow/admin/security → get_security_log (path_parts[2]=='security')
// Returns: { security_events: [...], count }
export const getSecurityReport = (params?: { limit?: number }) =>
  api.get<{ security_events: AuditEntry[]; count: number }>('/admin/security', { params }).then((r) => r.data)

// GET /innovateflow/admin/organisations → list_organisations
// Returns: { organisations: [...], count }
export const getAdminOrganisations = () =>
  api.get<{ organisations: { org_id: string; name: string; organisation_type?: string; owner_id?: string; member_count: number }[]; count: number }>('/admin/organisations').then((r) => r.data)

// GET /innovateflow/admin/organisations/{org_id} → get_organisation
// Returns: { organisation: {...}, members: [...] }
export const getAdminOrganisation = (orgId: string) =>
  api.get<{ organisation: { org_id: string; name: string; organisation_type?: string; owner_id?: string }; members: { user_id: string; email: string; role: string }[] }>(`/admin/organisations/${orgId}`).then((r) => r.data)

// PATCH /innovateflow/admin/organisations/{org_id} → update_organisation
// Body: { name?, organisation_type? }
export const updateAdminOrganisation = (orgId: string, data: { name?: string; organisation_type?: string }) =>
  api.patch(`/admin/organisations/${orgId}`, data)

// POST /innovateflow/admin/organisations/{org_id}/members → add_org_member
// Body: { user_id, role? }
export const addAdminOrgMember = (orgId: string, data: { user_id: string; role?: string }) =>
  api.post(`/admin/organisations/${orgId}/members`, data)

// DELETE /innovateflow/admin/organisations/{org_id}/members/{user_id} → remove_org_member
export const removeAdminOrgMember = (orgId: string, userId: string) =>
  api.delete(`/admin/organisations/${orgId}/members/${userId}`)

// ---------------------------------------------------------------------------
// Discovery — discovery/handler.py
// ---------------------------------------------------------------------------

// POST /innovateflow/discovery/search → search_discovery (path_parts[2]=='search')
// Body: { query, size? }
// Returns: { results: [...], count }
export const searchDiscovery = (query: string, size?: number) =>
  api.post<{ results: DiscoveryResult[]; count: number }>('/discovery/search', { query, size }).then((r) => r.data.results)

// GET /innovateflow/discovery/requests → list_requests (path_parts[2]=='requests')
// Returns: { requests: [...], count }
export const getConnectionRequests = () =>
  api.get<{ requests: ConnectionRequest[]; count: number }>('/discovery/requests').then((r) => r.data.requests)

// POST /innovateflow/discovery/request → send_request (path_parts[2]=='request' SINGULAR)
// Body: { discovery_id, message }
// Returns: { request: {...} }
export const sendConnectionRequest = (data: { discovery_id: string; message: string }) =>
  api.post<{ request: ConnectionRequest }>('/discovery/request', data).then((r) => r.data.request)

// PUT /innovateflow/discovery/request/{request_id} → respond_to_request (path_parts[2]=='request' SINGULAR)
// Body: { decision: 'accepted'|'rejected' }
// Returns: { message: 'Request accepted/rejected' }
export const respondToConnectionRequest = (requestId: string, decision: 'accepted' | 'rejected') =>
  api.put(`/discovery/request/${requestId}`, { decision })

// ---------------------------------------------------------------------------
// Journals — journals/handler.py (browse academic-embeddings index)
// ---------------------------------------------------------------------------

export interface JournalArticle {
  _id: string
  _score?: number
  title: string
  abstract?: string
  authors?: string[]
  doi?: string
  topics?: string[]
  citation_count?: number
  peer_reviewed?: boolean
  open_access?: boolean
  resource_type?: string
  publication_date?: string
  journal_name?: string
}

export interface TopicFacet {
  topic: string
  count: number
  relevance?: number
}

// GET /innovateflow/journals/topics?size=50&parent=X&project_id=Y → list topic facets
// project_id activates domain-boost weighting (1.5x match, 0.5x non-match) server-side
export const getJournalTopics = (params?: { size?: number; parent?: string; project_id?: string }) =>
  api.get<{ topics: TopicFacet[]; total_journals: number; parent: string | null }>(
    '/journals/topics', { params }
  ).then((r) => r.data)

// GET /innovateflow/journals/browse?topic=X&size=20&from=0&sort=relevance → browse by topic
export const browseJournals = (params: { topic: string; size?: number; from?: number; sort?: string }) =>
  api.get<{ journals: JournalArticle[]; total: number; topic: string; sub_topics: TopicFacet[]; from: number; size: number }>(
    '/journals/browse', { params }
  ).then((r) => r.data)

// GET /innovateflow/journals/{id} → get single journal
export const getJournal = (journalId: string) =>
  api.get<{ journal: JournalArticle }>(`/journals/${journalId}`).then((r) => r.data.journal)

// POST /innovateflow/journals/search → search journals
// BUG-NEW-X (Round 6): pass optional project_id so the backend can boost
// matches in the project's domain_classification, counteracting the
// energy-heavy seed corpus skew on cross-domain queries.
export const searchJournals = (data: { query: string; size?: number; topic?: string; project_id?: string }) =>
  api.post<{ journals: JournalArticle[]; total: number; topic_facets: TopicFacet[] }>(
    '/journals/search', data
  ).then((r) => r.data)

// ---------------------------------------------------------------------------
// Novelty Analysis
// ---------------------------------------------------------------------------

// POST /innovateflow/novelty/{documentId} → trigger novelty analysis
// BUG-NEW-N: ``force`` bypasses the content/correction cache — used by
// fullRerunMutation so Run Analysis re-evaluates corrections even when the
// underlying document text hasn't changed.
export const requestNoveltyAnalysis = (documentId: string, force = false, section?: string) => {
  const params = new URLSearchParams()
  if (force) params.set('force', 'true')
  // Stream B3 (#2783 / BUG-119): transmit the user's chosen grant invention
  // section so a re-run analyses the section they picked instead of silently
  // re-deriving it. The novelty backend honours this end-to-end: handler.py
  // reads ?section= into `user_section` and passes it as `preferred_label` to
  // shared.doc_type.extract_grant_invention_section, so the choice takes effect.
  if (section) params.set('section', section)
  const qs = params.toString()
  return api
    .post<{ novelty_report: NoveltyReport }>(`/novelty/${documentId}${qs ? `?${qs}` : ''}`)
    .then((r) => r.data.novelty_report)
}

// GET /innovateflow/novelty/{documentId} → get existing report
export const getNoveltyReport = (documentId: string) =>
  api.get<{ novelty_report: NoveltyReport | null }>(`/novelty/${documentId}`).then((r) => r.data.novelty_report)

// ---------------------------------------------------------------------------
// White-Space Discovery — Story #4937 Stream 1/2 (lambda/innovateflow-api/whitespace/)
// ---------------------------------------------------------------------------

import type {
  WhitespaceScanRun,
  WhitespaceScanResult,
  WhitespaceRegionsResponse,
  WhitespaceRegionsStatus,
  WhitespaceRegion,
  WhitespaceMapResponse,
  WhitespaceMapRun,
  WhitespaceMap3DResponse,
  WhitespaceMap3DRun,
  WhitespaceCompetitorsResponse,
  WhitespaceProbeRequest,
  WhitespaceProbeResponse,
  WhitespaceWatch,
  WhitespaceWatchListResponse,
  PortfolioSynergyResponse,
  PortfolioConceptNote,
  PortfolioNotifyBothLeadsResponse,
  WhitespacePanelCreateResponse,
  WhitespacePanelGetResponse,
  PortfolioEntitlementResponse,
  PortfolioMapResponse,
  PortfolioTopologyResponse,
  PortfolioShellsResponse,
  PortfolioScoreResponse,
  PortfolioWatch,
  PortfolioWatchListResponse,
  PortfolioWatchDeleteResponse,
  PortfolioPressureResponse,
  PortfolioBriefResponse,
  OverlapResponse,
  CachedOverlapResponse,
  OverlapComputeRun,
  OverlapGroupDetailResponse,
  OverlapFundingRollup,
  OverlapGroupStatusTarget,
  OverlapSetGroupStatusResponse,
  OverlapGroupNoteResponse,
  OverlapNotifyPreview,
  OverlapNotifyResult,
  OverlapGroupBriefResponse,
  PortfolioScope,
  AcrossPortfoliosResponse,
} from '../types/whitespace'
import { corpusCoverageNote, type WhitespaceMatrix } from '../lib/whitespace'

// POST /innovateflow/whitespace/scan/{project_id} → kick off an async density/
// opportunity scan (?force=true to bypass a complete cached result). Async
// from day one (RED-TEAM amendment C): the density scan against the full
// corpus will not fit API Gateway's 29s window, so this returns 202 + a run
// handle immediately (or 200 + cached:true on a cache hit) — poll
// getWhitespaceScan(run_id) for completion, novelty's self-invoke
// async-worker pattern (handler.py:121-124).
export const requestWhitespaceScan = (projectId: string, force = false): Promise<WhitespaceScanRun> =>
  api.post<WhitespaceScanRun>(`/whitespace/scan/${projectId}${force ? '?force=true' : ''}`).then((r) => r.data)

// GET /innovateflow/whitespace/scan/{run_id} → poll scan status; carries regions once complete.
export const getWhitespaceScan = (runId: string): Promise<WhitespaceScanResult> =>
  api.get<WhitespaceScanResult>(`/whitespace/scan/${runId}`).then((r) => r.data)

// GET /innovateflow/whitespace/regions/{project_id} → latest completed scan's regions.
// corpus_coverage_note is NOT sent by the backend — it's a client-side caveat
// string (lib/whitespace.ts) the AC requires always be visible.
export const getWhitespaceRegions = (projectId: string): Promise<WhitespaceRegionsResponse> =>
  api
    .get<{ project_id: string; run_id: string | null; status: WhitespaceRegionsStatus; completed_at?: string | null; regions: WhitespaceRegion[] | null }>(
      `/whitespace/regions/${projectId}`,
    )
    .then((r) => ({ ...r.data, corpus_coverage_note: corpusCoverageNote(r.data.regions?.length ?? 0) }))

// POST /innovateflow/whitespace/watch → create a watched region (Story #4937
// Stream 4, lambda/innovateflow-api/whitespace/watchlist.py:create_watch).
// Requires edit access to the project — a 403 here means the caller can view
// but not edit, and is surfaced to the user rather than silently swallowed.
export const createWhitespaceWatch = (params: {
  project_id: string
  corpus: string
  band: [number, number]
  record_ids: string[]
  wos_score?: number
}): Promise<WhitespaceWatch> => api.post<WhitespaceWatch>('/whitespace/watch', params).then((r) => r.data)

// GET /innovateflow/whitespace/watch → list the AUTHENTICATED caller's own
// watched regions (watchlist.py:list_watches — a Query on owner_id, never a
// Scan; rows for a project the caller has since lost view access to are
// dropped server-side).
export const listWhitespaceWatches = (): Promise<WhitespaceWatch[]> =>
  api.get<WhitespaceWatchListResponse>('/whitespace/watch').then((r) => r.data.watches)

// DELETE /innovateflow/whitespace/watch/{watchId} → remove a watched region
// (watchlist.py:delete_watch — the lookup key always uses the authenticated
// caller's own user_id, so this can never reach another owner's row).
export const deleteWhitespaceWatch = (watchId: string): Promise<void> =>
  api.delete(`/whitespace/watch/${watchId}`).then(() => undefined)

// ---------------------------------------------------------------------------
// White-Space Discovery — Landscape Map (Story #4937 Stream 3a/3b)
// ---------------------------------------------------------------------------

// GET /innovateflow/whitespace/map/{project_id} → latest cached 2-D layout.
export const getWhitespaceMap = (projectId: string): Promise<WhitespaceMapResponse> =>
  api.get<WhitespaceMapResponse>(`/whitespace/map/${projectId}`).then((r) => r.data)

// POST /innovateflow/whitespace/map/{project_id} → compute (or return a cached)
// 2-D projection layout. Synchronous — no polling needed, unlike /scan
// (whitespace/handler.py's STREAM 3a docstring: this endpoint's own OpenSearch
// work stays to a handful of small, bounded fetches).
export const requestWhitespaceMap = (projectId: string, force = false): Promise<WhitespaceMapRun> =>
  api.post<WhitespaceMapRun>(`/whitespace/map/${projectId}${force ? '?force=true' : ''}`).then((r) => r.data)

// ---------------------------------------------------------------------------
// White-Space Discovery — 3-D Explorer (Story #5116 Stream 8a/8d)
// ---------------------------------------------------------------------------

// GET /innovateflow/whitespace/3d/{project_id} → latest cached 3-D layout.
export const getWhitespace3D = (projectId: string): Promise<WhitespaceMap3DResponse> =>
  api.get<WhitespaceMap3DResponse>(`/whitespace/3d/${projectId}`).then((r) => r.data)

// POST /innovateflow/whitespace/3d/{project_id} → compute (or return a cached)
// 3-D projection layout — a genuine third orthogonal component (dims=3
// PCA fit), not a z=0 pad of the 2-D layout. Synchronous, same rationale
// as /map (whitespace/handler.py's STREAM 8a docstring).
export const requestWhitespace3D = (projectId: string, force = false): Promise<WhitespaceMap3DRun> =>
  api.post<WhitespaceMap3DRun>(`/whitespace/3d/${projectId}${force ? '?force=true' : ''}`).then((r) => r.data)

// ---------------------------------------------------------------------------
// White-Space Discovery — Competitor proximity (Story #5116 Stream 8b)
// ---------------------------------------------------------------------------

// GET /innovateflow/whitespace/competitors/{project_id} → competitor clouds
// + nearest-competitor-per-region, derived from the project's already-cached
// /scan (whitespace/detectors.py's get_competitor_clusters). Feeds the
// dimension rig's "Competitor proximity" attribute channel (Task #5156).
export const getWhitespaceCompetitors = (projectId: string): Promise<WhitespaceCompetitorsResponse> =>
  api.get<WhitespaceCompetitorsResponse>(`/whitespace/competitors/${projectId}`).then((r) => r.data)

// ---------------------------------------------------------------------------
// White-Space Discovery — Directional probes (Story #4937 Stream 3c)
// ---------------------------------------------------------------------------

// POST /innovateflow/whitespace/probe/{project_id} → generate (mode:'llm') or
// embed (mode:'freetext') one or more directional probes and land them on the map.
export const requestWhitespaceProbe = (
  projectId: string,
  body: WhitespaceProbeRequest,
): Promise<WhitespaceProbeResponse> =>
  api.post<WhitespaceProbeResponse>(`/whitespace/probe/${projectId}`, body).then((r) => r.data)

// POST /innovateflow/whitespace/probe/{project_id}/accept → What-Next handoff
// for an accepted white-space direction (a probe landing or a scored region).
// Story #5305 §5.4: "Accepting a probe now carries the probe text into a
// stored, encrypted, member-scoped record with a visible receipt — today it
// is silently discarded." whitespace/probes.py::accept_opportunity requires
// `text` (400s without it) and persists position/nearest_neighbour_distance/
// closest_record_similarity/mode alongside it; `analysis_run_id` in the
// response IS the visible receipt ProbePanel.tsx now shows.
export interface WhitespaceAcceptOpportunityBody {
  text: string
  position?: [number, number] | null
  nearest_neighbour_distance?: number | null
  closest_record_similarity?: number | null
  mode?: string
}
export const acceptWhitespaceOpportunity = (
  projectId: string,
  body: WhitespaceAcceptOpportunityBody,
): Promise<{ project_id: string; accepted: boolean; analysis_run_id: string }> =>
  api.post(`/whitespace/probe/${projectId}/accept`, body).then((r) => r.data)

// ---------------------------------------------------------------------------
// White-Space Discovery — Combination Matrix v2 (Story #5305 Task #5324
// Stream 10b backend, Task #5326 Stream 11b frontend). GET-only, view-gated
// — the matrix computes from the project's own most recent COMPLETE /scan
// cache, never triggers a fresh scan or decomposition itself.
// ---------------------------------------------------------------------------

// GET /innovateflow/whitespace/matrix/{project_id} → the feature x domain
// combination grid (whitespace/matrix.py::get_matrix). `status` distinguishes
// 'complete' from the honest 'no_components' / 'no_scan_yet' /
// 'no_domain_metadata' non-error states CombinationMatrix.tsx renders.
export const getWhitespaceMatrix = (projectId: string): Promise<WhitespaceMatrix> =>
  api.get<WhitespaceMatrix>(`/whitespace/matrix/${projectId}`).then((r) => r.data)

// POST the matrix's own `decompose_action.route` (design §5.3's no-components
// CTA — "Extract features first · uses AI credits") — the SAME
// crosspollination/handler.py::_decompose route CrossPollinationTab already
// calls, just reached from here for the matrix's own empty state. The server
// hands back an absolute `/innovateflow/...` path (crosspollination/handler.py
// and whitespace/matrix.py share that convention), but `api`'s own baseURL
// already carries `/innovateflow` — strip it so the two don't double up.
export const requestWhitespaceDecompose = (route: string): Promise<unknown> =>
  api.post(route.replace(/^\/innovateflow/, '')).then((r) => r.data)

// ---------------------------------------------------------------------------
// Story #5648 (Graduated UX) — the ONE scope primitive every portfolio,
// overlap and /portfolio-projects fetcher below uses. Three lanes each
// independently invented `?scope=own` before this was written down; this
// helper is what stops every one of the ~24 fetchers spelling it slightly
// differently. Own scope is requested by the query parameter `scope=own`
// alone — never a user id, sub or email (see the request interceptor above
// for the header side of the own-scope contract). Every existing call site's
// default behaviour (aggregator scope, X-Aggregator-Id header set from an
// explicit id or omitted) is UNCHANGED by this helper — it is additive.
// ---------------------------------------------------------------------------
export const scopedRequest = (
  scope: PortfolioScope | undefined,
  aggregatorId?: string | null,
  params?: Record<string, string>,
): { params?: Record<string, string>; headers?: Record<string, string> } => {
  if (scope === 'own') {
    return { params: { ...params, scope: 'own' } }
  }
  return {
    params,
    headers: aggregatorId ? { 'X-Aggregator-Id': aggregatorId } : undefined,
  }
}

// ---------------------------------------------------------------------------
// White-Space Discovery — Portfolio Synergy Matrix (Story #5115 Stream 5c,
// Task #5119 backend / Task #5122 frontend). Aggregator-scoped, same
// X-Aggregator-Id header convention as the aggregator/* endpoints below —
// the request interceptor also injects it from localStorage, but every
// call site here still accepts an explicit id so a caller can be sure it is
// scoping the exact aggregator context it just resolved.
// ---------------------------------------------------------------------------

// GET /innovateflow/whitespace/portfolio_synergy → stitched cross-project
// synergy matrix over the caller's real portfolio entitlement set.
// ?force=true bypasses compute_pair_synergy's 24h cache (portfolio_synergy.py).
export const getPortfolioSynergy = (
  aggregatorId?: string | null,
  force = false,
  scope?: PortfolioScope,
): Promise<PortfolioSynergyResponse> =>
  api
    .get<PortfolioSynergyResponse>(
      '/whitespace/portfolio_synergy',
      scopedRequest(scope, aggregatorId, force ? { force: 'true' } : undefined),
    )
    .then((r) => r.data)

// POST /innovateflow/whitespace/portfolio_synergy/concept_note → draft a
// joint concept note for a synergy cell's project pair (a new row in the
// existing whitespace-watch table — portfolio_synergy.py:draft_joint_concept_note).
// 404s (not 403) when either project isn't in the caller's real entitlement set.
export const draftPortfolioConceptNote = (
  projectAId: string,
  projectBId: string,
  title: string,
  content: string,
  aggregatorId?: string | null,
  scope?: PortfolioScope,
): Promise<PortfolioConceptNote> =>
  api
    .post<PortfolioConceptNote>(
      '/whitespace/portfolio_synergy/concept_note',
      { project_a_id: projectAId, project_b_id: projectBId, title, content },
      scopedRequest(scope, aggregatorId),
    )
    .then((r) => r.data)

// POST /innovateflow/whitespace/portfolio_synergy/notify_both_leads → sends
// two real notifications, one per project owner (portfolio_synergy.py:notify_both_leads).
export const notifyPortfolioSynergyLeads = (
  projectAId: string,
  projectBId: string,
  aggregatorId?: string | null,
  scope?: PortfolioScope,
): Promise<PortfolioNotifyBothLeadsResponse> =>
  api
    .post<PortfolioNotifyBothLeadsResponse>(
      '/whitespace/portfolio_synergy/notify_both_leads',
      { project_a_id: projectAId, project_b_id: projectBId },
      scopedRequest(scope, aggregatorId),
    )
    .then((r) => r.data)

// ---------------------------------------------------------------------------
// White-Space Discovery — Portfolio Atlas + Topology & Strategy (Story #5115
// Stream 6f, Task #5131). Same aggregator-scoped X-Aggregator-Id header
// convention as the portfolio_synergy methods above.
// ---------------------------------------------------------------------------

// GET /innovateflow/whitespace/portfolio → the caller's full portfolio
// entitlement set (whitespace/portfolio_entitlement.py::assemble_portfolio) —
// own_org_project_ids, granted_projects, shared_in_sources.
export const getPortfolioEntitlement = (
  aggregatorId?: string | null,
  scope?: PortfolioScope,
): Promise<PortfolioEntitlementResponse> =>
  api
    .get<PortfolioEntitlementResponse>('/whitespace/portfolio', scopedRequest(scope, aggregatorId))
    .then((r) => r.data)

// GET /innovateflow/whitespace/portfolio_map → the joint corpus-anchored 2-D
// projection of every entitlement-set member project's documents (Task #5126).
export const getPortfolioMap = (
  aggregatorId?: string | null,
  scope?: PortfolioScope,
): Promise<PortfolioMapResponse> =>
  api
    .get<PortfolioMapResponse>('/whitespace/portfolio_map', scopedRequest(scope, aggregatorId))
    .then((r) => r.data)

// GET /innovateflow/whitespace/portfolio_topology → full-space-validated
// holes/bridges/frontier edges over the portfolio's stitched layout (Task #5129).
export const getPortfolioTopology = (
  aggregatorId?: string | null,
  scope?: PortfolioScope,
): Promise<PortfolioTopologyResponse> =>
  api
    .get<PortfolioTopologyResponse>('/whitespace/portfolio_topology', scopedRequest(scope, aggregatorId))
    .then((r) => r.data)

// GET /innovateflow/whitespace/portfolio_shells → the 3-D Explorer's
// overlap-mode data (Story #5305 Task #5339): per-project alpha-shape
// shells at two snugness settings, full-space-confirmed shell
// intersections, and stability-gated hole findings with a real 3-D
// centroid. Same aggregator-scoped shape as portfolio_map/portfolio_topology
// above; cached server-side, so a repeat call for the same entitled set is
// cheap.
export const getPortfolioShells = (
  aggregatorId?: string | null,
  scope?: PortfolioScope,
): Promise<PortfolioShellsResponse> =>
  api
    .get<PortfolioShellsResponse>('/whitespace/portfolio_shells', scopedRequest(scope, aggregatorId))
    .then((r) => r.data)

// GET /innovateflow/whitespace/portfolio_score → WOS-P: Task #5129's
// validated topology candidates scored with the 2 new sub-scores (enclosure,
// synergy) alongside the 4 reused WOS ones (Task #5130).
export const getPortfolioScore = (
  aggregatorId?: string | null,
  scope?: PortfolioScope,
): Promise<PortfolioScoreResponse> =>
  api
    .get<PortfolioScoreResponse>('/whitespace/portfolio_score', scopedRequest(scope, aggregatorId))
    .then((r) => r.data)

// ---------------------------------------------------------------------------
// White-Space Discovery — Portfolio Watch + Brief Export (Story #5115
// Stream 7a/7b/7c backend, Task #5138 frontend). Same aggregator-scoped
// X-Aggregator-Id header convention as the portfolio_synergy/topology/score
// methods above.
// ---------------------------------------------------------------------------

// POST /innovateflow/whitespace/portfolio_watch → create a portfolio-scoped
// watch (whitespace/portfolio_watchlist.py:create_watch, Task #5133). A 404
// here means the caller has no aggregator context or no readable project in
// the requested scope — surfaced to the user, never silently swallowed
// (portfolio_watchlist.py's amendment D 404-on-deny convention).
export const createPortfolioWatch = (
  params: {
    member_project_ids: string[]
    corpus: string
    band: [number, number]
    record_ids: string[]
    wos_score?: number
  },
  aggregatorId?: string | null,
  scope?: PortfolioScope,
): Promise<PortfolioWatch> =>
  api
    .post<PortfolioWatch>('/whitespace/portfolio_watch', params, scopedRequest(scope, aggregatorId))
    .then((r) => r.data)

// GET /innovateflow/whitespace/portfolio_watch → list the AUTHENTICATED
// caller's own portfolio watches (portfolio_watchlist.py:list_watches — a
// Query on owner_id, never a Scan; a row whose aggregator access has since
// been revoked in any way is dropped server-side, not surfaced as a leak).
// Story #5648 (A1(e)): own and aggregator watch rows share one table keyed
// owner_id/watch_id, so sending nothing is ambiguous rather than neutral —
// the client must state which set it is asking for, even though this
// fetcher never sent an aggregator id in the first place.
// Overloaded (rather than a single `scope?: PortfolioScope` parameter) so
// the zero-arg call signature stays structurally assignable to
// react-query's QueryFunction type — PortfolioWatchPanel.tsx (not A7's file)
// passes this fetcher directly as `queryFn: listPortfolioWatches`, and a
// single optional-param signature is NOT assignable to that callback type
// (TS checks same-arity parameters contravariantly even when optional).
export function listPortfolioWatches(): Promise<PortfolioWatch[]>
export function listPortfolioWatches(scope: PortfolioScope): Promise<PortfolioWatch[]>
export function listPortfolioWatches(scope?: PortfolioScope): Promise<PortfolioWatch[]> {
  return api
    .get<PortfolioWatchListResponse>('/whitespace/portfolio_watch', scopedRequest(scope, null))
    .then((r) => r.data.watches)
}

// DELETE /innovateflow/whitespace/portfolio_watch/{watchId} → remove a
// portfolio watch (portfolio_watchlist.py:delete_watch — the lookup key
// always uses the authenticated caller's own user_id, so this can never
// reach another owner's row). Same A1(e) scope-discriminator requirement
// as listPortfolioWatches above.
export const deletePortfolioWatch = (
  watchId: string,
  scope?: PortfolioScope,
): Promise<PortfolioWatchDeleteResponse> =>
  api
    .delete<PortfolioWatchDeleteResponse>(`/whitespace/portfolio_watch/${watchId}`, scopedRequest(scope, null))
    .then((r) => r.data)

// GET /innovateflow/whitespace/portfolio_pressure → rolling 90-day
// per-competitor encroachment-pressure counters (Task #5134's rescan writes
// them; Task #5138 added this route since none existed to read them back —
// see whitespace/portfolio_rescan.py:get_portfolio_pressure).
export const getPortfolioPressure = (
  aggregatorId?: string | null,
  scope?: PortfolioScope,
): Promise<PortfolioPressureResponse> =>
  api
    .get<PortfolioPressureResponse>('/whitespace/portfolio_pressure', scopedRequest(scope, aggregatorId))
    .then((r) => r.data)

// GET /innovateflow/whitespace/portfolio_brief → assembles a whole .docx
// portfolio brief (whitespace/portfolio_brief.py:get_portfolio_brief, Task
// #5136), returned base64-encoded inside the JSON envelope. Callers must
// decode + trigger a real file download — see lib/whitespace.ts's
// downloadBase64File, which this route's own UI (PortfolioWatchPanel.tsx)
// uses rather than just logging success.
export const getPortfolioBrief = (
  aggregatorId?: string | null,
  scope?: PortfolioScope,
): Promise<PortfolioBriefResponse> =>
  api
    .get<PortfolioBriefResponse>('/whitespace/portfolio_brief', scopedRequest(scope, aggregatorId))
    .then((r) => r.data)

// ---------------------------------------------------------------------------
// White-Space Discovery — Portfolio Overlap / fund-once (Story #5305, Task
// #5317). Same aggregator-scoped X-Aggregator-Id header convention as the
// portfolio_synergy/topology/score methods above. Routes are Stream 13d's
// (Task #5309) — POST …/compute, GET …/portfolio_overlap, GET …/portfolio_
// overlap/group/{id} — per that task's own Steps; a 404 on the group route
// is design §6.3's own byte-identical "no longer available" convention, so
// callers must not distinguish it from any other 404 here.
// ---------------------------------------------------------------------------

// GET /innovateflow/whitespace/portfolio_overlap → the caller's per-viewer,
// ⊆-filtered overlap view (design §6.2). A cache miss reads back
// `status: 'recomputing'` and the backend enqueues a run server-side — this
// call never computes pair mathematics itself.
export const getPortfolioOverlap = (
  aggregatorId?: string | null,
  scope?: PortfolioScope,
): Promise<OverlapResponse> =>
  api
    .get<OverlapResponse>('/whitespace/portfolio_overlap', scopedRequest(scope, aggregatorId))
    .then((r) => r.data)

// GET /innovateflow/whitespace/portfolio_overlap?scope=own&no_enqueue=1 → the
// CACHE-ONLY read (Story #5648 task #5717, contract row S3).
//
// Why this exists as a SEPARATE fetcher rather than a flag on the one above:
// getPortfolioOverlap enqueues a run when the cache misses — by design, for the
// portfolio tab, where the user has asked to see overlap. Contract row S3
// forbids the Invention scope from ever triggering that: opening one invention
// must not start pair mathematics across the user's whole set. Giving the
// enqueueing fetcher an extra argument would leave one function whose safety
// depends on every future call site passing it correctly; a distinct function
// cannot be called wrongly by omission.
//
// The backend returns BEFORE _start_overlap_compute on this path
// (whitespace/handler.py `_no_enqueue_requested`), so nothing is created: no
// overlap-run row, no worker invocation. `cached` distinguishes a real computed
// result from the empty-on-miss one — an empty `groups` alone is ambiguous,
// because a complete run whose groups are all filtered out by the per-viewer
// subset re-check also yields [].
//
// Published by lane A5 after lane A7 was archived and ownership of
// services/api.ts transferred (recorded on #5717); shape and conventions follow
// A7's — scope via scopedRequest, never a user id on the wire.
export const getCachedPortfolioOverlap = (
  scope?: PortfolioScope,
  aggregatorId?: string | null,
): Promise<CachedOverlapResponse> =>
  api
    .get<CachedOverlapResponse>(
      '/whitespace/portfolio_overlap',
      scopedRequest(scope, aggregatorId, { no_enqueue: '1' }),
    )
    .then((r) => r.data)

// POST /innovateflow/whitespace/portfolio_overlap/compute → explicit re-run
// (design §6.2: "open to any active team member for explicit re-runs,
// rate-limited by a cooldown whose bypass signal is server-derived"). The
// first-open auto-enqueue is entirely server-side — this method is only for
// the card's own "Re-run" link.
export const computePortfolioOverlap = (
  aggregatorId?: string | null,
  scope?: PortfolioScope,
): Promise<OverlapComputeRun> =>
  api
    .post<OverlapComputeRun>('/whitespace/portfolio_overlap/compute', undefined, scopedRequest(scope, aggregatorId))
    .then((r) => r.data)

// GET /innovateflow/whitespace/portfolio_overlap/group/{groupId} → full
// group detail (members, evidence rows, funding chips). 404s byte-
// identically whether the id never existed, the group decayed, or the
// viewer's access narrowed — the caller must render one uniform message
// for all three (design §6.3).
export const getPortfolioOverlapGroup = (
  groupId: string,
  aggregatorId?: string | null,
  scope?: PortfolioScope,
): Promise<OverlapGroupDetailResponse> =>
  api
    .get<OverlapGroupDetailResponse>(
      `/whitespace/portfolio_overlap/group/${groupId}`,
      scopedRequest(scope, aggregatorId),
    )
    .then((r) => r.data)

// GET /innovateflow/whitespace/portfolio_overlap/funding → design §6.3's
// "Same funding programme mentioned by multiple projects" left-rail card.
// Scoped to the calling viewer's own current entitlement server-side —
// see OverlapFundingRollup's own doc comment.
export const getPortfolioOverlapFunding = (
  aggregatorId?: string | null,
  scope?: PortfolioScope,
): Promise<OverlapFundingRollup> =>
  api
    .get<OverlapFundingRollup>('/whitespace/portfolio_overlap/funding', scopedRequest(scope, aggregatorId))
    .then((r) => r.data)

// ---------------------------------------------------------------------------
// Overlap lifecycle + fund-once workbench — Story #5305 Stream 14b, Task
// #5318 (design §6.3/§6.4). Same X-Aggregator-Id convention as every other
// portfolio_overlap method above.
// ---------------------------------------------------------------------------

// POST .../group/{id}/status → durable lifecycle status (overlap_lifecycle.
// set_group_status, Stream 13e). 403 when the target status needs admin/
// billing-owner and the caller is below that floor; 404 on an unknown or
// de-entitled group — same byte-identical convention every route here uses.
export const setOverlapGroupStatus = (
  groupId: string,
  status: OverlapGroupStatusTarget,
  aggregatorId?: string | null,
  scope?: PortfolioScope,
): Promise<OverlapSetGroupStatusResponse> =>
  api
    .post<OverlapSetGroupStatusResponse>(
      `/whitespace/portfolio_overlap/group/${groupId}/status`,
      { status },
      scopedRequest(scope, aggregatorId),
    )
    .then((r) => r.data)

// GET .../group/{id}/note → the encrypted lifecycle note (this task's own
// route, overlap_prose.get_note underneath). `available: false` is a real,
// expected state (no organisation key registered) — render `copy`, not an
// error banner.
export const getOverlapGroupNote = (
  groupId: string,
  aggregatorId?: string | null,
  scope?: PortfolioScope,
): Promise<OverlapGroupNoteResponse> =>
  api
    .get<OverlapGroupNoteResponse>(
      `/whitespace/portfolio_overlap/group/${groupId}/note`,
      scopedRequest(scope, aggregatorId),
    )
    .then((r) => r.data)

// POST .../group/{id}/note → write the note (design §6.3: "any active team
// member can set reviewing and add the note" — no extra role floor here
// beyond aggregator membership, enforced server-side).
export const putOverlapGroupNote = (
  groupId: string,
  note: string,
  aggregatorId?: string | null,
  scope?: PortfolioScope,
): Promise<OverlapGroupNoteResponse> =>
  api
    .post<OverlapGroupNoteResponse>(
      `/whitespace/portfolio_overlap/group/${groupId}/note`,
      { note },
      scopedRequest(scope, aggregatorId),
    )
    .then((r) => r.data)

// POST .../group/{id}/notify {preview:true} → the confirm step's verbatim
// text + recipient list (overlap_notify.preview_notify_leads, Stream 13h).
// Read-only — no side effects, so the confirm dialog can call this freely.
//
// Own scope returns a DIFFERENT shape than OverlapNotifyPreview above —
// verified live 4 Sep 2026 as bob (zero memberships, "you are the lead on
// every project here"):
//   {group_id, own_scope: true, recipients: [], notified_owner_ids: [],
//    reason: "You are the lead on every project here"}
// (no `message`/`floor_capped` fields). overlap_notify._notify_leads_impl
// takes this branch identically for preview=true AND preview=false (see
// notifyOverlapLeads below) — own scope has nobody to raise the group
// WITH, so it is a stated no-op rather than a 404, and it writes no
// overlap-notify-sent row and no audit row either way. A UI consuming
// this for own scope must branch on `own_scope` rather than assuming
// OverlapNotifyPreview's fields are present.
export const previewNotifyOverlapLeads = (
  groupId: string,
  aggregatorId?: string | null,
  scope?: PortfolioScope,
): Promise<OverlapNotifyPreview> =>
  api
    .post<OverlapNotifyPreview>(
      `/whitespace/portfolio_overlap/group/${groupId}/notify`,
      { preview: true },
      scopedRequest(scope, aggregatorId),
    )
    .then((r) => r.data)

// POST .../group/{id}/notify {} → the real, one-shot send (overlap_notify.
// notify_leads). 409 when this group has already been raised with its
// leads — the caller renders that as an honest "already sent" state, never
// a retry. Own scope returns the SAME no-op shape as previewNotifyOverlapLeads
// above (verified live — same call, same response, no write either way);
// see that fetcher's comment for the exact body and why it is safe.
export const notifyOverlapLeads = (
  groupId: string,
  aggregatorId?: string | null,
  scope?: PortfolioScope,
): Promise<OverlapNotifyResult> =>
  api
    .post<OverlapNotifyResult>(
      `/whitespace/portfolio_overlap/group/${groupId}/notify`,
      {},
      scopedRequest(scope, aggregatorId),
    )
    .then((r) => r.data)

// POST .../group/{id}/brief → the consolidated funding case .docx (design
// §6.4, portfolio_brief.post_group_brief, Task #5313). Same base64→Blob
// idiom as getPortfolioBrief — see PortfolioWatchPanel.tsx's
// downloadBase64File, this task's own Key Files pointer.
//
// KNOWN BACKEND DEFECT (own scope only) — verified live 4 Sep 2026, filed
// as ADO Bug #5825: this route 500s for an own-scope caller
// ('NoneType' object has no attribute 'get' in
// portfolio_brief._assemble_group_brief_data, agg_ctx is None reaching
// post_group_brief) even though the sibling own-scope routes on the same
// portfolio_overlap block (status/note/notify/group GET) all resolve
// agg_ctx correctly and return 200. This client call is correct — url,
// method, body and scope param all verified via the captured request
// config — the gap is server-side own-scope agg_ctx resolution specific
// to the brief POST branch. See tests/live-evidence/5648-a7-overlap-group.
// json's known_defects block for the full traceback.
export const getOverlapGroupBrief = (
  groupId: string,
  ask?: string,
  aggregatorId?: string | null,
  scope?: PortfolioScope,
): Promise<OverlapGroupBriefResponse> =>
  api
    .post<OverlapGroupBriefResponse>(
      `/whitespace/portfolio_overlap/group/${groupId}/brief`,
      ask ? { ask } : {},
      scopedRequest(scope, aggregatorId),
    )
    .then((r) => r.data)

// ---------------------------------------------------------------------------
// White-Space Discovery — Analysis Panel (Story #5116 Streams 9c-9e,
// lambda/innovateflow-api/whitespace/panel.py)
// ---------------------------------------------------------------------------

// POST /innovateflow/whitespace/panel/{project_id} → run (or return a cached)
// multi-vendor consensus panel over a set of ALREADY-RESOLVED boundary
// record ids — sourced directly from an Opportunity Explorer card's own
// patent_record_ids/academic_record_ids (never a probe_id or band key,
// whitespace/panel.py's create_panel docstring). ?force=true bypasses a
// complete cached result, same convention as /map and /3d.
export const requestAnalysisPanel = (
  projectId: string,
  recordIds: string[],
  force = false,
): Promise<WhitespacePanelCreateResponse> =>
  api
    .post<WhitespacePanelCreateResponse>(`/whitespace/panel/${projectId}${force ? '?force=true' : ''}`, {
      record_ids: recordIds,
    })
    .then((r) => r.data)

// GET /innovateflow/whitespace/panel/{project_id}/{content_hash} → the same
// cached panel result requestAnalysisPanel computed, keyed by project_id
// FIRST — never a bare content-hash (panel.py's module docstring on the
// cross-tenant leak this cache-key shape fixes).
export const getAnalysisPanel = (projectId: string, contentHash: string): Promise<WhitespacePanelGetResponse> =>
  api.get<WhitespacePanelGetResponse>(`/whitespace/panel/${projectId}/${contentHash}`).then((r) => r.data)

// ---------------------------------------------------------------------------
// Recommendations & Portfolio
// ---------------------------------------------------------------------------

// GET /innovateflow/recommendations → get user profile with recommendations and clusters
export const getRecommendations = () =>
  api.get<{ profile: UserProfile | null }>('/recommendations').then((r) => r.data.profile)

// POST /innovateflow/recommendations/refresh → trigger manual refresh
export const refreshRecommendations = () =>
  api.post<{ profile: UserProfile }>('/recommendations/refresh').then((r) => r.data.profile)

// ---------------------------------------------------------------------------
// Patent Search
// ---------------------------------------------------------------------------

export interface PatentSearchParams {
  query?: string
  /** #4612: "other patents by this inventor" — matches the inventors field. */
  inventor?: string
  /** #4612/#4646: "other patents by this assignee" — matches the assignee field. */
  assignee?: string
  cpc_codes?: string[]
  jurisdiction?: string
  date_from?: string
  date_to?: string
  max_results?: number
  /** #4612: server-side pagination offset — page through the full match set
   * instead of a capped 100. `total` in the response is the true match count. */
  from?: number
}

export interface PatentSearchResult {
  patent_id: string
  title: string
  abstract: string
  cpc_codes: string[]
  filing_date: string
  publication_date: string
  inventors: string[]
  assignee: string
  jurisdiction: string
  source_api: string
  score?: number
  /** #4612: link-out to the full patent on Google Patents. */
  google_patents_url?: string
  /** LANGDETECT-AA (Wave 3c): ISO 639-1 source language code when non-English. */
  source_lang?: string
}

// POST /innovateflow/patents/search → search patents
export const searchPatents = (params: PatentSearchParams) =>
  api.post<{ results: PatentSearchResult[]; total: number }>('/patents/search', params).then((r) => r.data)

// POST /innovateflow/patents/compare → compare patent with user's invention
export const compareWithInvention = (patentId: string, documentId: string) =>
  api.post<{ novelty_report: NoveltyReport }>('/patents/compare', { patent_id: patentId, document_id: documentId }).then((r) => r.data.novelty_report)

// Patent Browse — browse patents by CPC-derived topic categories

export interface PatentBrowseResult {
  _id: string
  _score: number | null
  patent_id: string
  title: string
  abstract: string
  cpc_codes: string[]
  topics: string[]
  jurisdiction: string
  filing_date: string
  publication_date: string
  inventors: string[]
  assignee: string
  source_api: string
  /** #4612: link-out to the full patent on Google Patents. */
  google_patents_url?: string
  /** LANGDETECT-AA (Wave 3c): ISO 639-1 source language code. Non-null when non-English. */
  source_lang?: string
}

export interface JurisdictionFacet {
  jurisdiction: string
  count: number
}

// GET /innovateflow/patents/topics?size=50&parent=X → list CPC topic facets
export const getPatentTopics = (params?: { size?: number; parent?: string }) =>
  api.get<{ topics: TopicFacet[]; total_patents: number; parent: string | null }>(
    '/patents/topics', { params }
  ).then((r) => r.data)

// GET /innovateflow/patents/browse?topic=X&size=20&from=0&sort=relevance → browse by topic
export const browsePatents = (params: { topic: string; size?: number; from?: number; sort?: string; jurisdiction?: string }) =>
  api.get<{ patents: PatentBrowseResult[]; total: number; topic: string; sub_topics: TopicFacet[]; jurisdictions: JurisdictionFacet[]; from: number; size: number }>(
    '/patents/browse', { params }
  ).then((r) => r.data)

// GET /innovateflow/patents/{id} → get single patent
export const getPatent = (patentId: string) =>
  api.get<{ patent: PatentBrowseResult }>(`/patents/${patentId}`).then((r) => {
    // Task #3326-r4 fix 1e: explicitly throw when the backend returns 200 with
    // an unexpected shape (no `patent` field) so React Query sets isError=true
    // and the detail panel can render the not-found banner.  Without this
    // guard a soft-200 produces an undefined query result and the panel
    // renders blank.
    if (!r.data || !r.data.patent) {
      throw Object.assign(new Error('Patent not found'), { response: { status: 404 } })
    }
    return r.data.patent
  })

// #4612/#4646: Patent Connections graph — matches ConnectionsGraph's
// pluggable fetchExplore/fetchDiscover shape ({nodes,edges,center} /
// {nodes,edges,expanded_from}) exactly, so the graph component works
// unmodified against either data source.
// GET /innovateflow/patents/{id}/connections → get_patent_connections
export const getPatentConnections = (patentId: string) =>
  api.get<{ nodes: ConnectionsNode[]; edges: ConnectionsEdge[]; center: string; summary?: PatentConnectionsSummary }>(
    `/patents/${patentId}/connections`
  ).then((r) => r.data)

// GET /innovateflow/patents/{id}/connections/discover?node_id=&node_type= →
// discover_patent_connections. The `id` in the path is the ORIGINAL centre
// patent for the tab (kept for a stable/bookmarkable URL); the node actually
// expanded is node_id/node_type, which may be a different patent already
// present in the graph (e.g. a similar_patent neighbour).
export const discoverPatentConnections = (patentId: string, nodeId: string, nodeType: string) =>
  api.get<{ nodes: ConnectionsNode[]; edges: ConnectionsEdge[]; expanded_from: string; summary?: PatentConnectionsSummary }>(
    `/patents/${patentId}/connections/discover`, { params: { node_id: nodeId, node_type: nodeType } }
  ).then((r) => r.data)

export interface PatentConnectionsSummary {
  similar_count: number
  inventor_count: number
  assignee_count: number
  family_count: number
  top_similar: string[]
}

// Story #3409: on-demand patent translation.
export interface PatentTranslationResult {
  title: string
  abstract: string
  claims: string[]
  target_lang: string
  source_lang_detected: string
  cached: boolean
  direction: 'ltr' | 'rtl'
}

// POST /innovateflow/patents/{id}/translate → translate title/abstract/claims
// mode='quick': use AWS Translate (faster/cheaper); omit for Bedrock Claude (accurate).
// QUICK-TRANSLATE-AA (Wave 3c Task #3657).
export const translatePatent = (patentId: string, targetLang: string, mode?: 'quick') =>
  api
    .post<PatentTranslationResult>(`/patents/${encodeURIComponent(patentId)}/translate`, {
      target_lang: targetLang,
      ...(mode ? { mode } : {}),
    })
    .then((r) => r.data)

// LANGDETECT-C5: translate a journal title via AWS Translate (quick mode, ap-southeast-2).
export const translateJournalTitle = (text: string, sourceLang: string) =>
  api
    .post<{ translated_text: string; source_lang_detected: string }>('/journals/translate', {
      text,
      source_lang: sourceLang,
    })
    .then((r) => r.data)

// ---------------------------------------------------------------------------
// Disclosure — disclosure/handler.py
// ---------------------------------------------------------------------------

// GET /innovateflow/disclosure/agreements → list agreements for current user's org
// Optional projectId scopes results to agreements for a specific project.
export const getDisclosureAgreements = (projectId?: string) =>
  projectId
    ? api.get<{ agreements: DisclosureAgreement[] }>('/disclosure/agreements', { params: { project_id: projectId } }).then((r) => r.data.agreements)
    : api.get<{ agreements: DisclosureAgreement[] }>('/disclosure/agreements').then((r) => r.data.agreements)

// GET /innovateflow/disclosure/agreement/{id} → get single agreement
export const getDisclosureAgreement = (agreementId: string) =>
  api.get<{ agreement: DisclosureAgreement }>(`/disclosure/agreement/${agreementId}`).then((r) => r.data.agreement)

// GET /innovateflow/disclosure/agreements?aggregator_id={id} → filter by aggregator
export const getAggregatorDisclosures = (aggregatorId: string): Promise<DisclosureAgreement[]> =>
  api.get<{ agreements: DisclosureAgreement[] }>('/disclosure/agreements', { params: { aggregator_id: aggregatorId } }).then((r) => r.data.agreements)

// POST /innovateflow/disclosure/request-advance → request next level
// Accepts either a targetOrgId (creates/gets agreement) or an existing agreementId
export const requestDisclosureAdvance = (params: { targetOrgId?: string; agreementId?: string }) => {
  const body = params.agreementId
    ? { agreement_id: params.agreementId }
    : { target_org_id: params.targetOrgId }
  return api.post<{ agreement: DisclosureAgreement }>('/disclosure/request-advance', body).then((r) => r.data.agreement)
}

// POST /innovateflow/crosspollination/express-interest/{projectId} → create initial agreement
// Caller passes their OWN project; backend resolves hidden partner (US-2 double-blind semantics)
export const expressInterest = (projectId: string) =>
  api.post<{ agreement: DisclosureAgreement }>(`/crosspollination/express-interest/${projectId}`).then((r) => r.data.agreement)

// GET /innovateflow/disclosure/content/{agreementId} → get unlocked disclosure content
// BUG-091: the backend route is /disclosure/agreement/{id}/content (the old
// /disclosure/content/{id} path has no handler → 404). Repointed so this helper
// works if wired; live UI uses getGatedDisclosureContent below.
export const getDisclosureContent = (agreementId: string) =>
  api.get<{ content: string; level: number }>(`/disclosure/agreement/${agreementId}/content`).then((r) => r.data)

// GET /innovateflow/disclosure/nda-url/{agreementId} → get NDA signing URL
export const getNdaSigningUrl = (agreementId: string) =>
  api.get<{ url: string; expires_at: string }>(`/disclosure/nda-url/${agreementId}`).then((r) => r.data)

// GET /innovateflow/disclosure/agreement/{id}/content → the real, level-gated
// disclosed content for this agreement (the counterparty project's own AI
// analysis, filtered by the caller's consented level — see DisclosureContent).
export const getDisclosureAgreementContent = (agreementId: string) =>
  api.get<{ content: DisclosureContent; level: number }>(`/disclosure/agreement/${agreementId}/content`).then((r) => r.data)

// POST /innovateflow/disclosure/consent → consent to advance
export const consentDisclosureAdvance = (agreementId: string) =>
  api.post<{ agreement: DisclosureAgreement }>('/disclosure/consent', { agreement_id: agreementId }).then((r) => r.data.agreement)

// POST /innovateflow/disclosure/decline → decline a pending advance (counterparty only).
// A real server-side rejection — clears the pending advance so both screens agree (B1-01).
export const declineDisclosureAdvance = (agreementId: string) =>
  api.post<{ agreement: DisclosureAgreement }>('/disclosure/decline', { agreement_id: agreementId }).then((r) => r.data.agreement)

// POST /innovateflow/disclosure/revoke → revoke to lower level
export const revokeDisclosureLevel = (agreementId: string, targetLevel: number) =>
  api.post<{ agreement: DisclosureAgreement }>('/disclosure/revoke', { agreement_id: agreementId, target_level: targetLevel }).then((r) => r.data.agreement)

// GET /innovateflow/disclosure/agreement/{id}/verify-commitments → verify tamper-evident chain
export const verifyDisclosureCommitments = (agreementId: string) =>
  api.get<{
    chain: Array<{
      level: number
      expected_commitment: string
      stored_commitment: string
      verified: boolean
      history_hash_match: boolean
      chain_link_match: boolean
      signed?: boolean
      org_a_sig_valid?: boolean | null
      org_b_sig_valid?: boolean | null
    }>
    all_verified: boolean
  }>(`/disclosure/agreement/${agreementId}/verify-commitments`).then(r => r.data)

// GET /innovateflow/disclosure/agreement/{id}/audit-bundle → downloadable audit bundle
export const getDisclosureAuditBundle = (agreementId: string) =>
  api.get<{
    agreement_id: string
    level_commitments: Array<unknown>
    history: Array<unknown>
    created_at: string
  }>(`/disclosure/agreement/${agreementId}/audit-bundle`).then(r => r.data)

// POST /innovateflow/disclosure/keypair — register Ed25519 public key (raw 32-byte hex)
export const registerDisclosureKeypair = (publicKeyHex: string): Promise<{ registered: boolean; public_key_hex: string; registered_at: string }> =>
  api.post('/disclosure/keypair', { public_key_hex: publicKeyHex }).then((r) => r.data)

// GET /innovateflow/disclosure/keypair — fetch registered public key status
export const getDisclosureKeypair = (): Promise<{ has_keypair: boolean; public_key_hex?: string; registered_at?: string }> =>
  api.get('/disclosure/keypair').then((r) => r.data)

// GET /innovateflow/disclosure/keypairs — list all active registered keys for the caller's org (Story #4015 S4)
export interface DisclosureKeypairItem {
  public_key_hex: string
  created_at?: string
  valid_from?: string
}
export const listDisclosureKeypairs = (): Promise<DisclosureKeypairItem[]> =>
  api.get<{ keys: DisclosureKeypairItem[] }>('/disclosure/keypairs').then((r) => r.data.keys ?? [])

// DELETE /innovateflow/disclosure/keypair/{hex} — revoke a registered key (Story #4015 S4)
export const revokeDisclosureKeypair = (publicKeyHex: string): Promise<void> =>
  api.delete(`/disclosure/keypair/${publicKeyHex}`).then(() => undefined)

// GET /innovateflow/disclosure/agreement/{id}/prepare-commitment
export const prepareCommitment = (agreementId: string): Promise<{
  agreement_id: string
  next_level: number
  payload: Record<string, unknown>
  payload_bytes_hex: string
  advanced_at: string
}> => api.get(`/disclosure/agreement/${agreementId}/prepare-commitment`).then(r => r.data)

// POST /innovateflow/disclosure/agreement/{id}/commit-signed
export const commitSigned = (
  agreementId: string,
  orgASigHex: string,
  orgBSigHex: string,
  expectedLevel: number,
  advancedAt?: string
): Promise<{ agreement_id: string; level: number; commitment_hex: string }> =>
  api.post(`/disclosure/agreement/${agreementId}/commit-signed`, {
    org_a_sig_hex: orgASigHex,
    org_b_sig_hex: orgBSigHex,
    expected_level: expectedLevel,
    ...(advancedAt ? { advanced_at: advancedAt } : {}),
  }).then(r => r.data)

// ---------------------------------------------------------------------------
// CRM — crm/handler.py (project-scoped contacts, orgs, interactions)
// ---------------------------------------------------------------------------

import type {
  CrmContact, CrmOrganisation, CrmContactOrg, CrmInteraction,
  CrmRelevantItem, CrmStats, ConvertAuthorRequest, ConvertAuthorResponse,
  CrmEntityAssociation, CrmPermission, CrmTodo, CrmEmailTemplate,
  CrmContactRelationship, CrmActivityItem, SendEmailRequest,
  CsvImportResult, DuplicateMatch, MergeContactsRequest,
} from '../types'

// Contacts
export const getCrmContacts = (projectId: string) =>
  api.get<{ contacts: CrmContact[]; count: number }>('/crm/contacts', { params: { project_id: projectId } }).then((r) => r.data)

export const getCrmContact = (contactId: string, projectId: string) =>
  api.get<{ contact: CrmContact }>(`/crm/contacts/${contactId}`, { params: { project_id: projectId } }).then((r) => r.data.contact)

export const createCrmContact = (data: Partial<CrmContact>) =>
  api.post<{ contact: CrmContact }>('/crm/contacts', data).then((r) => r.data.contact)

export const updateCrmContact = (contactId: string, data: Partial<CrmContact>) =>
  api.put<{ contact: CrmContact }>(`/crm/contacts/${contactId}`, data).then((r) => r.data.contact)

export const deleteCrmContact = (contactId: string, projectId: string) =>
  api.delete(`/crm/contacts/${contactId}`, { params: { project_id: projectId } })

// Organisations
export const getCrmOrgs = (projectId: string) =>
  api.get<{ organisations: CrmOrganisation[]; count: number }>('/crm/orgs', { params: { project_id: projectId } }).then((r) => r.data)

export const getCrmOrg = (orgId: string, projectId: string) =>
  api.get<{ organisation: CrmOrganisation }>(`/crm/orgs/${orgId}`, { params: { project_id: projectId } }).then((r) => r.data.organisation)

export const createCrmOrg = (data: Partial<CrmOrganisation>) =>
  api.post<{ organisation: CrmOrganisation }>('/crm/orgs', data).then((r) => r.data.organisation)

export const updateCrmOrg = (orgId: string, data: Partial<CrmOrganisation>) =>
  api.put<{ organisation: CrmOrganisation }>(`/crm/orgs/${orgId}`, data).then((r) => r.data.organisation)

export const deleteCrmOrg = (orgId: string, projectId: string) =>
  api.delete(`/crm/orgs/${orgId}`, { params: { project_id: projectId } })

// Contact-Org Links
export const getCrmContactOrgs = (projectId: string, contactId?: string, orgId?: string) =>
  api.get<{ contact_org_links: CrmContactOrg[]; count: number }>('/crm/contact-orgs', {
    params: { project_id: projectId, ...(contactId ? { contact_id: contactId } : {}), ...(orgId ? { org_id: orgId } : {}) }
  }).then((r) => r.data)

export const linkContactOrg = (data: { contact_id: string; org_id: string; project_id: string; role_at_org?: string }) =>
  api.post<{ contact_org_link: CrmContactOrg }>('/crm/contact-orgs', data).then((r) => r.data.contact_org_link)

export const unlinkContactOrg = (contactId: string, orgId: string) =>
  api.delete(`/crm/contact-orgs/${contactId}/${orgId}`)

// Interactions
export const getCrmInteractions = (projectId: string, targetId?: string) =>
  api.get<{ interactions: CrmInteraction[]; count: number }>('/crm/interactions', {
    params: { project_id: projectId, ...(targetId ? { target_id: targetId } : {}) }
  }).then((r) => r.data)

export const createCrmInteraction = (data: Partial<CrmInteraction>) =>
  api.post<{ interaction: CrmInteraction }>('/crm/interactions', data).then((r) => r.data.interaction)

export const updateCrmInteraction = (interactionId: string, data: Partial<CrmInteraction>) =>
  api.put<{ interaction: CrmInteraction }>(`/crm/interactions/${interactionId}`, data).then((r) => r.data.interaction)

export const deleteCrmInteraction = (interactionId: string) =>
  api.delete(`/crm/interactions/${interactionId}`)

// Relevant Items
export const getCrmRelevantItems = (projectId: string, targetId?: string) =>
  api.get<{ relevant_items: CrmRelevantItem[]; count: number }>('/crm/relevant-items', {
    params: { project_id: projectId, ...(targetId ? { target_id: targetId } : {}) }
  }).then((r) => r.data)

export const createCrmRelevantItem = (data: Partial<CrmRelevantItem>) =>
  api.post<{ relevant_item: CrmRelevantItem }>('/crm/relevant-items', data).then((r) => r.data.relevant_item)

export const deleteCrmRelevantItem = (itemId: string) =>
  api.delete(`/crm/relevant-items/${itemId}`)

// Author Conversion
export const convertAuthor = (data: ConvertAuthorRequest) =>
  api.post<ConvertAuthorResponse>('/crm/convert-author', data).then((r) => r.data)

// Stats
export const getCrmStats = (projectId: string) =>
  api.get<{ stats: CrmStats }>('/crm/stats', { params: { project_id: projectId } }).then((r) => r.data.stats)

// Collaboration Rollup — project-level CRM + document/folder aggregation
export interface CollaborationRollupSuggestion {
  action_id: string
  document_id: string
  title: string
  description: string
  priority: number
  scope: string
  source_rule: string
  status: string
  created_at: string
  document_count?: number
  document_ids?: string[]
}

export interface CollaborationRollupResponse {
  contacts: CrmContact[]
  organisations: CrmOrganisation[]
  links: Array<{ link_id: string; title: string; type: 'journal' | 'patent'; source_document_id: string; notes: string; _source: string }>
  todos: CrmTodo[]
  suggestions: CollaborationRollupSuggestion[]
  contact_org_links: CrmContactOrg[]
  counts: { contacts: number; organisations: number; links: number; todos: number; suggestions: number }
}

export const getCollaborationRollup = (projectId: string) =>
  api.get<CollaborationRollupResponse>('/crm/collaboration-rollup', { params: { project_id: projectId } }).then((r) => r.data)

// =============================================================================
// Document Lists (saved connections per document)
// =============================================================================

export const getDocumentListItems = (
  documentId: string, type?: string
) =>
  api.get<{ items: DocumentListItem[]; count: number }>(
    `/documents/${documentId}/list`,
    { params: { ...(type ? { type } : {}) } }
  ).then((r) => r.data)

export const addDocumentListItem = (
  documentId: string, item: { resource_type: string; resource_id: string; title: string; notes?: string }
) =>
  api.post<{ item: DocumentListItem }>(`/documents/${documentId}/list`, item).then((r) => r.data.item)

export const removeDocumentListItem = (
  documentId: string, resourceType: string, resourceId: string
) =>
  api.delete(`/documents/${documentId}/list/${resourceType}/${resourceId}`)

// =============================================================================
// Folder Lists, Notes & Connections
// =============================================================================

// Folder List Items
export const getFolderListItems = (
  projectId: string, folderId: string, type?: string, aggregate?: boolean
) =>
  api.get<{ items: FolderListItem[]; count: number }>(
    `/folders/${projectId}/${folderId}/list`,
    { params: { ...(type ? { type } : {}), ...(aggregate ? { aggregate: 'true' } : {}) } }
  ).then((r) => r.data)

export const addFolderListItem = (
  projectId: string, folderId: string, item: { resource_type: string; resource_id: string; title: string; notes?: string }
) =>
  api.post<{ item: FolderListItem }>(`/folders/${projectId}/${folderId}/list`, item).then((r) => r.data.item)

export const removeFolderListItem = (
  projectId: string, folderId: string, resourceType: string, resourceId: string
) =>
  api.delete(`/folders/${projectId}/${folderId}/list/${resourceType}/${resourceId}`)

// Folder Notes
export const getFolderNotes = (projectId: string, folderId: string, aggregate?: boolean) =>
  api.get<{ notes: FolderNote[]; count: number }>(
    `/folders/${projectId}/${folderId}/notes`,
    { params: aggregate ? { aggregate: 'true' } : {} }
  ).then((r) => r.data)

export const createFolderNote = (
  projectId: string, folderId: string, content: string, visibility: 'private' | 'shared' = 'private'
) =>
  api.post<{ note: FolderNote }>(`/folders/${projectId}/${folderId}/notes`, { content, visibility }).then((r) => r.data.note)

export const updateFolderNote = (
  projectId: string, folderId: string, noteId: string, updates: { content?: string; visibility?: 'private' | 'shared' }
) =>
  api.put<{ note: FolderNote }>(`/folders/${projectId}/${folderId}/notes/${noteId}`, updates).then((r) => r.data.note)

export const deleteFolderNote = (projectId: string, folderId: string, noteId: string) =>
  api.delete(`/folders/${projectId}/${folderId}/notes/${noteId}`)

// Folder Connections Graph
export const getFolderConnections = (
  projectId: string, folderId: string, depth: number, sources?: string[], mode?: string
) =>
  api.get<FolderConnectionsData>(
    `/folders/${projectId}/${folderId}/connections`,
    { params: { depth, ...(sources?.length ? { sources: sources.join(',') } : {}), ...(mode ? { mode } : {}) } }
  ).then((r) => r.data)

// Folder Settings
export const updateFolderSettings = (
  projectId: string, folderId: string, settings: { aggregate_children?: boolean }
) =>
  api.patch<{ folder: Folder }>(`/folders/${projectId}/${folderId}`, settings).then((r) => r.data.folder)

// =============================================================================
// CMK — Customer Managed Encryption Keys (cmk-api Lambda handlers)
// =============================================================================

export const getCMKKeys = () =>
  api.get('/cmk/keys').then((r) => r.data)

export const createPlatformKey = (migrateExisting?: boolean, scopeType?: string, scopeId?: string) =>
  api.post('/cmk/create-key', { migrate_existing: migrateExisting, scope_type: scopeType, scope_id: scopeId }).then((r) => r.data)

export const registerCustomerKey = (kmsKeyArn: string, migrateExisting?: boolean, scopeType?: string, scopeId?: string) =>
  api.post('/cmk/register-key', { kms_key_arn: kmsKeyArn, migrate_existing: migrateExisting, scope_type: scopeType, scope_id: scopeId }).then((r) => r.data)

export const revokeCMKKey = (kmsKeyArn: string, scopeType?: string, scopeId?: string) =>
  api.post('/cmk/revoke-key', { kms_key_arn: kmsKeyArn, scope_type: scopeType, scope_id: scopeId }).then((r) => r.data)

export const getEffectiveKey = (resourceType: string, resourceId: string) =>
  api.get(`/cmk/effective-key/${resourceType}/${resourceId}`).then((r) => r.data)

// --- Custody tier (four-tier model) + WebAuthn step-up -----------------------
// The custody tier is a org-level control; escalating into/out of a
// WebAuthn-protected tier requires a step-up token minted by the WebAuthn
// ceremony below. Routes are served by cmk_routes (innovateflow-api).

export interface CmkTier {
  tier: 'standard' | 'enhanced' | 'premium' | 'sovereign'
  name: string
  features: string[]
  requires_webauthn: boolean
  allows_byok: boolean
  allows_xks: boolean
  available_transitions: Array<'standard' | 'enhanced' | 'premium' | 'sovereign'>
  /** Story #6490: false for a non-admin member — the same check POST /cmk/tier enforces. */
  can_change?: boolean
  tenant_id?: string
  previous_tier?: string
  updated_at?: string
  message?: string
}

export const getCmkTier = () =>
  api.get<CmkTier>('/cmk/tier').then((r) => r.data)

export const updateCmkTier = (targetTier: string, webauthnToken?: string) =>
  api.post<CmkTier>('/cmk/tier', { target_tier: targetTier, webauthn_token: webauthnToken }).then((r) => r.data)

// WebAuthn step-up ceremony (the browser glue lives in services/webauthnClient.ts).
// begin returns the PublicKeyCredential*Options JSON (+ challenge_id); complete
// posts the authenticator response back for verification.
export const webauthnRegisterBegin = () =>
  api.post('/cmk/webauthn/register/begin', {}).then((r) => r.data)

export const webauthnRegisterComplete = (credential: unknown, challengeId: string) =>
  api.post('/cmk/webauthn/register/complete', { credential, challenge_id: challengeId }).then((r) => r.data)

export const webauthnAuthBegin = (operation: string) =>
  api.post('/cmk/webauthn/auth/begin', { operation }).then((r) => r.data)

export const webauthnAuthComplete = (credential: unknown, challengeId: string) =>
  api.post<{ webauthn_token: string; expires_in: number }>(
    '/cmk/webauthn/auth/complete', { credential, challenge_id: challengeId }).then((r) => r.data)

// =============================================================================
// CRM Extended — Entity Associations (User Story #1197)
// =============================================================================

export const createEntityAssociation = (data: Partial<CrmEntityAssociation>) =>
  api.post<{ association: CrmEntityAssociation }>('/crm/entity-associations', data).then((r) => r.data.association)

export const getEntityAssociations = (entityType: string, entityId: string) =>
  api.get<{ associations: CrmEntityAssociation[]; count: number }>('/crm/entity-associations', {
    params: { entity_type: entityType, entity_id: entityId }
  }).then((r) => r.data)

export const deleteEntityAssociation = (entityTypeId: string, assocTypeId: string) =>
  api.delete(`/crm/entity-associations/${encodeURIComponent(entityTypeId)}/${encodeURIComponent(assocTypeId)}`)

export const getMyContacts = () =>
  api.get<{ contacts: CrmContact[]; count: number }>('/crm/my-contacts').then((r) => r.data)

export const getMyOrgs = () =>
  api.get<{ organisations: CrmOrganisation[]; count: number }>('/crm/my-orgs').then((r) => r.data)

// =============================================================================
// CRM Extended — Permissions
// =============================================================================

export const getCrmPermissions = (projectId: string, userId?: string) =>
  api.get<{ permissions: CrmPermission[] }>('/crm/permissions', {
    params: { project_id: projectId, ...(userId ? { user_id: userId } : {}) }
  }).then((r) => r.data.permissions)

export const updateCrmPermissions = (projectId: string, userId: string, data: Partial<CrmPermission>) =>
  api.put<{ permission: CrmPermission }>(`/crm/permissions/${projectId}/${userId}`, data).then((r) => r.data.permission)

export const deleteCrmPermissions = (projectId: string, userId: string) =>
  api.delete(`/crm/permissions/${projectId}/${userId}`)

export const getEffectiveCrmPermissions = (projectId: string, userId: string) =>
  api.get<{ permission: CrmPermission }>(`/crm/permissions/${projectId}/${userId}/effective`).then((r) => r.data.permission)

// =============================================================================
// CRM Extended — To-Dos
// =============================================================================

export const getCrmTodos = (entityTypeId?: string, assigneeId?: string, status?: string) =>
  api.get<{ todos: CrmTodo[]; count: number }>('/crm/todos', {
    params: { ...(entityTypeId ? { entity_type_id: entityTypeId } : {}), ...(assigneeId ? { assignee_id: assigneeId } : {}), ...(status ? { status } : {}) }
  }).then((r) => r.data)

export const getCrmTodo = (entityTypeId: string, todoId: string) =>
  api.get<{ todo: CrmTodo }>(`/crm/todos/${encodeURIComponent(entityTypeId)}/${todoId}`).then((r) => r.data.todo)

export const createCrmTodo = (data: Partial<CrmTodo>) =>
  api.post<{ todo: CrmTodo }>('/crm/todos', data).then((r) => r.data.todo)

export const updateCrmTodo = (entityTypeId: string, todoId: string, data: Partial<CrmTodo>) =>
  api.put<{ todo: CrmTodo }>(`/crm/todos/${encodeURIComponent(entityTypeId)}/${todoId}`, data).then((r) => r.data.todo)

export const deleteCrmTodo = (entityTypeId: string, todoId: string) =>
  api.delete(`/crm/todos/${encodeURIComponent(entityTypeId)}/${todoId}`)

// =============================================================================
// CRM Extended — Email Templates
// =============================================================================

export const getEmailTemplates = () =>
  api.get<{ templates: CrmEmailTemplate[]; count: number }>('/crm/email-templates').then((r) => r.data)

export const getEmailTemplate = (templateId: string) =>
  api.get<{ template: CrmEmailTemplate }>(`/crm/email-templates/${templateId}`).then((r) => r.data.template)

export const createEmailTemplate = (data: Partial<CrmEmailTemplate>) =>
  api.post<{ template: CrmEmailTemplate }>('/crm/email-templates', data).then((r) => r.data.template)

export const updateEmailTemplate = (templateId: string, data: Partial<CrmEmailTemplate>) =>
  api.put<{ template: CrmEmailTemplate }>(`/crm/email-templates/${templateId}`, data).then((r) => r.data.template)

export const deleteEmailTemplate = (templateId: string) =>
  api.delete(`/crm/email-templates/${templateId}`)

// =============================================================================
// CRM Extended — Email Sending
// =============================================================================

export const sendCrmEmail = (data: SendEmailRequest) =>
  api.post<{ interaction: CrmInteraction; message_id: string }>('/crm/send-email', data).then((r) => r.data)

export const previewEmailTemplate = (templateId: string, mergeData: Record<string, string>) =>
  api.post<{ subject: string; body: string }>('/crm/email-templates/preview', { template_id: templateId, merge_data: mergeData }).then((r) => r.data)

// =============================================================================
// CRM Extended — Contact Relationships
// =============================================================================

export const getContactRelationships = (contactId: string) =>
  api.get<{ relationships: CrmContactRelationship[]; count: number }>(`/crm/contacts/${contactId}/relationships`).then((r) => r.data)

export const createContactRelationship = (data: Partial<CrmContactRelationship>) =>
  api.post<{ relationship: CrmContactRelationship }>('/crm/contact-relationships', data).then((r) => r.data.relationship)

export const deleteContactRelationship = (contactId: string, relatedContactId: string) =>
  api.delete(`/crm/contact-relationships/${contactId}/${relatedContactId}`)

export const getReverseRelationships = (contactId: string) =>
  api.get<{ relationships: CrmContactRelationship[]; count: number }>(`/crm/contacts/${contactId}/reverse-relationships`).then((r) => r.data)

// =============================================================================
// CRM Extended — Activity Views
// =============================================================================

export const getCrmActivity = (params: { group_by?: 'entity' | 'date'; entity_type?: string; entity_id?: string; limit?: number }) =>
  api.get<{ activities: CrmActivityItem[]; count: number }>('/crm/activity', { params }).then((r) => r.data)

export const getContactActivity = (contactId: string) =>
  api.get<{ activities: CrmActivityItem[]; count: number }>(`/crm/contacts/${contactId}/activity`).then((r) => r.data)

export const getOrgActivity = (orgId: string) =>
  api.get<{ activities: CrmActivityItem[]; count: number }>(`/crm/orgs/${orgId}/activity`).then((r) => r.data)

// =============================================================================
// CRM Extended — Import/Export & Dedup
// =============================================================================

// BUG-089: the backend (crm/import_export.py) reads a JSON body with a base64
// `csv_data` string + `column_mapping` — not multipart/form-data. Read the file
// and send the contract the backend actually parses.
export const importContactsCsv = async (projectId: string, file: File, columnMapping: Record<string, string>) => {
  const text = await file.text()
  const csv_data = btoa(unescape(encodeURIComponent(text)))
  const r = await api.post<CsvImportResult>('/crm/import/contacts', {
    project_id: projectId,
    csv_data,
    column_mapping: columnMapping,
  })
  return r.data
}

export const exportContactsCsv = (projectId: string) =>
  api.get('/crm/export/contacts', { params: { project_id: projectId }, responseType: 'blob' }).then((r) => r.data)

export const exportContactVcard = (contactId: string) =>
  api.get(`/crm/export/contacts/${contactId}/vcard`, { responseType: 'blob' }).then((r) => r.data)

export const detectDuplicates = (projectId: string) =>
  api.get<{ duplicates: DuplicateMatch[][]; count: number }>('/crm/dedup/detect', { params: { project_id: projectId } }).then((r) => r.data)

export const mergeContacts = (data: MergeContactsRequest) =>
  api.post<{ contact: CrmContact }>('/crm/dedup/merge', data).then((r) => r.data.contact)

// =============================================================================
// Orchestrator API (Phase 1 — What Next Engine + Intake)
// =============================================================================

export const getWhatNext = (documentId: string): Promise<SuggestedAction[]> =>
  api.get<{ actions: SuggestedAction[] }>(`/whatnext/${documentId}`).then((r) => r.data.actions)

export const refreshWhatNext = (documentId: string): Promise<SuggestedAction[]> =>
  api.post<{ actions: SuggestedAction[] }>(`/whatnext/${documentId}/refresh`).then((r) => r.data.actions)

export const updateWhatNextAction = (
  documentId: string,
  actionId: string,
  status: 'accepted' | 'dismissed'
): Promise<SuggestedAction> =>
  api.put<{ action: SuggestedAction }>(`/whatnext/${documentId}/${actionId}`, { status }).then((r) => r.data.action)

export const getProjectWhatNext = (projectId: string): Promise<import('../types').ProjectWhatNextResponse> =>
  api.get<import('../types').ProjectWhatNextResponse>(`/whatnext/project/${projectId}`).then((r) => r.data)

// BUG-RETEST-I STREAM11B: accept a strategic What Next suggestion
export const acceptWhatNextSuggestion = (
  projectId: string,
  suggestionId: string,
  actionType: 'request_review' | 'plan_interviews' | 'search_grants' | 'start_patent_filing',
): Promise<{ action_type: string; todo_id?: string; idempotent?: boolean; grants_search?: unknown }> =>
  api
    .post(`/whatnext/project/${projectId}/accept`, { action_type: actionType, suggestion_id: suggestionId })
    .then((r) => r.data)

export const submitIntakeAnswers = (
  documentId: string,
  answers: { intent: string; handling_constraints: string; document_relationship: string; related_document_id?: string; component_role?: string; vertical?: string }
): Promise<IntakeAnswers> =>
  api.post<IntakeAnswers>(`/intake/${documentId}`, answers).then((r) => r.data)

export const getIntakeAnswers = (documentId: string): Promise<{ intake_completed: boolean; answers: IntakeAnswers | null }> =>
  api.get(`/intake/${documentId}`).then((r) => r.data)

export const getIntakeOptions = (documentId: string, vertical?: string): Promise<{ options: IntakeOptions }> =>
  api.get(`/intake/${documentId}/options`, { params: vertical ? { vertical } : undefined }).then((r) => r.data)

// =============================================================================
// Ecosystem API (Phase 2 — Ecosystem Marketplace)
// =============================================================================

export const createEcosystemProfile = (data: Partial<EcosystemProfile>): Promise<EcosystemProfile> =>
  api.post<{ profile: EcosystemProfile }>('/ecosystem/profiles', data).then((r) => r.data.profile)

export const getEcosystemProfile = (profileId: string): Promise<EcosystemProfile> =>
  api.get<{ profile: EcosystemProfile }>(`/ecosystem/profiles/${profileId}`).then((r) => r.data.profile)

export const getMyEcosystemProfile = (userId: string): Promise<EcosystemProfile | null> =>
  api.get<{ profile: EcosystemProfile | null }>('/ecosystem/profiles', { params: { user_id: userId } }).then((r) => r.data.profile)

export const updateEcosystemProfile = (profileId: string, data: Partial<EcosystemProfile>): Promise<EcosystemProfile> =>
  api.put<{ profile: EcosystemProfile }>(`/ecosystem/profiles/${profileId}`, data).then((r) => r.data.profile)

export const deleteEcosystemProfile = (profileId: string): Promise<void> =>
  api.delete(`/ecosystem/profiles/${profileId}`).then(() => undefined)

export const searchEcosystemProfiles = (
  query?: string,
  filters?: EcosystemSearchFilters,
  limit?: number
): Promise<EcosystemProfile[]> =>
  api.post<{ profiles: EcosystemProfile[] }>('/ecosystem/search', { query, filters, limit }).then((r) => r.data.profiles)

export const matchEcosystemProfiles = (documentId: string): Promise<EcosystemMatch[]> =>
  api.post<{ matches: EcosystemMatch[] }>(`/ecosystem/match/${documentId}`).then((r) => r.data.matches)

// Unified match — merges ecosystem profiles + directory collaborators with prioritisation
export const unifiedMatchProfiles = (documentId: string): Promise<EcosystemMatch[]> =>
  api.post<{ matches: EcosystemMatch[] }>(`/ecosystem/unified-match/${documentId}`).then((r) => r.data.matches)

// Project-context ecosystem search — POST /ecosystem/search-by-project (Story #2232)
export const searchEcosystemByProject = (
  projectId: string,
  filters?: EcosystemSearchFilters,
  limit?: number
): Promise<{ matches: EcosystemProfile[]; project_synthesis: string; total: number }> =>
  api.post<{ matches: EcosystemProfile[]; project_synthesis: string; total: number }>('/ecosystem/search-by-project', { project_id: projectId, filters, limit }).then((r) => r.data)

// LinkedIn OAuth
export const getLinkedInAuthUrl = (redirectUri: string): Promise<{ url: string }> =>
  api.get('/ecosystem/linkedin/auth-url', { params: { redirect_uri: redirectUri } }).then((r) => r.data)

export const linkedInCallback = (code: string, redirectUri: string): Promise<{ profile: LinkedInProfile; suggested_skills: SkillCategory[] }> =>
  api.post('/ecosystem/linkedin/callback', { code, redirect_uri: redirectUri }).then((r) => r.data)

// Ecosystem Connections — real Connect action (POST /ecosystem/connect writes
// to ECOSYSTEM_CONNECTIONS_TABLE; distinct from sendConnectionRequest, which
// targets a discovery_id/document owner via /discovery/request).
export const connectEcosystemProfile = (profileId: string, message: string): Promise<EcosystemConnection> =>
  api.post<{ connection: EcosystemConnection }>('/ecosystem/connect', { profile_id: profileId, message }).then((r) => r.data.connection)

export const getEcosystemConnections = (): Promise<EcosystemConnection[]> =>
  api.get<{ connections: EcosystemConnection[]; count: number }>('/ecosystem/connections').then((r) => r.data.connections)

// =============================================================================
// Ratings & Reputation API (Phase 3)
// =============================================================================

export const submitRating = (data: {
  target_profile_id: string
  dimensions: RatingDimensions
  review_text?: string
  context_type: 'project_collaboration' | 'review' | 'consultation'
  tags?: string[]
}): Promise<Rating> =>
  api.post<{ rating: Rating }>('/ecosystem/rate', data).then((r) => r.data.rating)

export const getRatings = (profileId: string, limit?: number, offset?: number): Promise<{ ratings: Rating[]; total: number }> =>
  api.get(`/ecosystem/ratings/${profileId}`, { params: { limit, offset } }).then((r) => r.data)

export const getReputation = (profileId: string): Promise<Reputation> =>
  api.get<{ reputation: Reputation }>(`/ecosystem/reputation/${profileId}`).then((r) => r.data.reputation)

// Expert Review
export const requestExpertReview = (documentId: string): Promise<ExpertReview> =>
  api.post<{ review: ExpertReview }>(`/ecosystem/expert-review/brief/${documentId}`).then((r) => r.data.review)

export const getExpertReview = (documentId: string): Promise<ExpertReview | null> =>
  api.get<{ review: ExpertReview | null }>(`/ecosystem/expert-review/${documentId}`).then((r) => r.data.review)

export const getExpertReviewQuestions = (documentId: string): Promise<{ questions: ExpertReview['questions'] }> =>
  api.get(`/ecosystem/expert-review/questions/${documentId}`).then((r) => r.data)

export const submitExpertReview = (reviewId: string, answers: Record<string, string>): Promise<ExpertReview> =>
  api.post<{ review: ExpertReview }>(`/ecosystem/expert-review/submit/${reviewId}`, { answers }).then((r) => r.data.review)

export const matchExpertReviewers = (documentId: string): Promise<EcosystemProfile[]> =>
  api.post<{ reviewers: EcosystemProfile[] }>(`/ecosystem/expert-review/match/${documentId}`).then((r) => r.data.reviewers)

// =============================================================================
// Workflow API (Phase 4 — Flexible Workflow Engine)
// =============================================================================

export const enableWorkflow = (projectId: string): Promise<WorkflowInstance> =>
  api.post<{ instance: WorkflowInstance }>(`/workflow/enable/${projectId}`).then((r) => r.data.instance)

export const getWorkflowStatus = (projectId: string): Promise<WorkflowInstance | null> =>
  api.get<{ instance: WorkflowInstance | null }>(`/workflow/status/${projectId}`).then((r) => r.data.instance)

export const advanceWorkflow = (projectId: string): Promise<WorkflowInstance> =>
  api.post<{ instance: WorkflowInstance }>(`/workflow/advance/${projectId}`).then((r) => r.data.instance)

export const approveStage = (projectId: string, stageId: string): Promise<WorkflowInstance> =>
  api.post<{ instance: WorkflowInstance }>(`/workflow/approve/${projectId}/${stageId}`).then((r) => r.data.instance)

export const getWorkflowDefinition = (workflowId: string): Promise<WorkflowDefinition> =>
  api.get<{ definition: WorkflowDefinition }>(`/workflow/definition/${workflowId}`).then((r) => r.data.definition)

export const updateWorkflowDefinition = (workflowId: string, data: Partial<WorkflowDefinition>): Promise<WorkflowDefinition> =>
  api.put<{ definition: WorkflowDefinition }>(`/workflow/definition/${workflowId}`, data).then((r) => r.data.definition)

// The live backend (workflow/handler.py + workflow/assessment.py) returns
// `{ assessment: { axes: { novelty|feasibility|market_potential: {score: 0-10} } } }`
// (POST wraps run_three_axis_assessment; GET returns the stored assessment, or
// `{ assessment: null }` when none exists). The old `{ scores }` annotation
// described a shape the API never sends — decode with normalizeAssessmentScores
// (components/orchestrator/assessmentScores.ts), never by hand.
export const triggerAssessment = (projectId: string): Promise<AssessmentApiResponse> =>
  api.post(`/workflow/assessment/${projectId}`).then((r) => r.data)

export const getAssessment = (projectId: string): Promise<AssessmentApiResponse> =>
  api.get(`/workflow/assessment/${projectId}`).then((r) => r.data)

export interface FreemiumGateResult {
  allowed: boolean
  tier: string
  status?: string
  reason?: string
  upgrade_options?: { tier: string; price: string; features: string[] }[]
  current_usage?: { active_projects: number; max_projects: number; tier: string }
  features?: Record<string, unknown>
}

export const checkFreemiumGate = (projectId: string): Promise<FreemiumGateResult> =>
  api.get(`/workflow/freemium/${projectId}`).then((r) => r.data)

export const processPayment = (projectId: string, paymentToken: string): Promise<{ status: string }> =>
  api.post(`/workflow/freemium/${projectId}/pay`, { payment_token: paymentToken }).then((r) => r.data)

export const getBouncedFeedback = (projectId: string): Promise<{ feedback: Array<{ axis: string; score: number; advice: string }> }> =>
  api.get(`/workflow/bounced/${projectId}`).then((r) => r.data)

export const requestRework = (projectId: string): Promise<WorkflowInstance> =>
  api.post<{ instance: WorkflowInstance }>(`/workflow/bounced/${projectId}/rework`).then((r) => r.data.instance)

// =============================================================================
// Cross-Pollination API (Phase 5)
// =============================================================================

// GET /crosspollination/match-summary/{projectId}
// Returns ONLY count + status — no component titles, descriptions, scores, or matched IDs.
export const getMatchSummary = (projectId: string): Promise<MatchSummary> =>
  api.get<MatchSummary>(`/crosspollination/match-summary/${projectId}`).then((r) => r.data)

export const triggerCrossPollinationMatch = (projectId: string): Promise<void> =>
  api.post(`/crosspollination/match/${projectId}`).then(() => undefined)

export const getCrossPollinationComponents = (projectId: string): Promise<IdeaComponent[]> =>
  api.get<{ components: IdeaComponent[] }>(`/crosspollination/components/${projectId}`).then((r) => r.data.components)

export const grantCrossPollinationConsent = (projectId: string): Promise<void> =>
  api.post(`/crosspollination/consent/${projectId}`).then(() => undefined)

// =============================================================================
// Collaborator Directory API (Story #2083)
// =============================================================================

// Browse directory entities with pagination
export const browseDirectory = (params: DirectorySearchParams): Promise<{ entities: DirectoryEntity[]; total: number }> =>
  api.get('/directory/browse', { params }).then((r) => r.data)

// Search directory (keyword, semantic, or hybrid)
export const searchDirectory = (params: DirectorySearchParams): Promise<{ entities: DirectoryEntity[]; total: number }> =>
  api.get<{ entities: DirectoryEntity[]; total: number }>('/directory/search', { params }).then((r) => r.data)

// Get a single directory entity by ID
export const getDirectoryEntity = (entityId: string): Promise<DirectoryEntity> =>
  api.get<{ entity: DirectoryEntity }>(`/directory/entities/${entityId}`).then((r) => r.data.entity)

// Get faceted counts for directory filters
export const getDirectoryFacets = (): Promise<DirectoryFacets> =>
  api.get('/directory/facets').then((r) => {
    const f = r.data?.facets || r.data || {}
    return {
      entity_types: f.entity_type || f.entity_types || [],
      countries: f.country_code || f.countries || [],
      regions: f.region || f.regions || [],
      topics: f.topics || [],
      total: f.total || 0,
    }
  })

// Get publications for a directory entity
export const getDirectoryPublications = (entityId: string, page = 1, pageSize = 10): Promise<{ publications: DirectoryPublication[]; total: number }> =>
  api.get(`/directory/entities/${entityId}/publications`, { params: { page, page_size: pageSize } }).then((r) => r.data)

// Get co-authors for a directory entity
export const getDirectoryCoAuthors = (entityId: string): Promise<{ co_authors: DirectoryCoAuthor[] }> =>
  api.get(`/directory/entities/${entityId}/co-authors`).then((r) => r.data)

// Geo search — find entities near a location
// BUG-090: directory/handler.py serves geo-search as GET and reads
// queryStringParameters — a POST body is ignored (405). Send a GET with params.
export const geoSearchDirectory = (params: DirectoryGeoSearch): Promise<{ entities: DirectoryEntity[]; total: number }> =>
  api.get<{ entities: DirectoryEntity[]; total: number }>('/directory/geo-search', {
    params: params as unknown as Record<string, string | number | boolean | undefined>,
  }).then((r) => r.data)

// Copy a directory entity into a project
export const copyToProject = (entityId: string, projectId: string): Promise<DirectoryCopy> =>
  api.post<{ copy: DirectoryCopy }>('/directory/copies', { entity_id: entityId, project_id: projectId }).then((r) => r.data.copy)

// List copies in a project
export const listCopies = (projectId: string): Promise<{ copies: DirectoryCopy[]; count: number }> =>
  api.get<{ copies: DirectoryCopy[]; count: number }>('/directory/copies', { params: { project_id: projectId } }).then((r) => r.data)

// Resync a single entity copy
export const resyncEntity = (copyId: string): Promise<DirectoryCopy> =>
  api.post<{ copy: DirectoryCopy }>(`/directory/copies/${copyId}/resync`).then((r) => r.data.copy)

// Resync all copies in a project
export const resyncAll = (projectId: string): Promise<{ synced: number }> =>
  api.post<{ synced: number }>('/directory/resync', { project_id: projectId }).then((r) => r.data)

// Unlink (delete) a directory copy
export const unlinkEntity = (copyId: string): Promise<void> =>
  api.delete(`/directory/copies/${copyId}`).then(() => undefined)

// Check for updates across all copies in a project
export const checkUpdates = (projectId: string): Promise<{ stale: number; copies: DirectoryCopy[] }> =>
  api.get<{ stale: number; copies: DirectoryCopy[] }>('/directory/check-updates', { params: { project_id: projectId } }).then((r) => r.data)

// Match a document against directory entities
export const matchDocument = (documentId: string): Promise<DirectoryMatchResult[]> =>
  api.post<{ matches: DirectoryMatchResult[] }>(`/directory/match-document/${documentId}`).then((r) => r.data.matches)

// Get existing document matches
export const getDocumentMatches = (documentId: string): Promise<DirectoryMatchResult[]> =>
  api.get<{ matches: DirectoryMatchResult[] }>(`/directory/matches/${documentId}`).then((r) => r.data.matches)

// Match all documents in a project
export const matchProject = (projectId: string): Promise<{ matched: number }> =>
  api.post<{ matched: number }>(`/directory/match-project/${projectId}`).then((r) => r.data)

// ---------------------------------------------------------------------------
// AI Analysis & Prior Art — novelty/handler.py (Story #2233)
// ---------------------------------------------------------------------------

// GET /innovateflow/novelty/project/{projectId}/analysis → get project AI analysis
export const getProjectAnalysis = (projectId: string): Promise<ProjectAIAnalysis | null> =>
  api.get<{ ai_analysis: ProjectAIAnalysis | null }>(`/novelty/project/${projectId}`).then((r) => r.data.ai_analysis)

// POST /innovateflow/novelty/project/{projectId} → request project AI analysis
export const requestProjectAnalysis = (projectId: string): Promise<ProjectAIAnalysis> =>
  api.post<{ ai_analysis: ProjectAIAnalysis }>(`/novelty/project/${projectId}`).then((r) => r.data.ai_analysis)

// PATCH /innovateflow/novelty/project/{projectId} → update project AI analysis with corrections
export const updateProjectAnalysis = (projectId: string, corrections: Partial<ProjectAIAnalysis>): Promise<ProjectAIAnalysis> =>
  api.patch<{ ai_analysis: ProjectAIAnalysis }>(`/novelty/project/${projectId}`, corrections).then((r) => r.data.ai_analysis)

// GET /innovateflow/novelty/project/{projectId}/claims → Story #5648
// Task #5738 (A4's route). The claim->supporting-documents edge, computed
// server-side at READ TIME over the reader's entitled documents and
// re-filtered per reader (never persisted) -- see ProjectClaim's own
// comment in types/index.ts for why this is not a field on ClaimAnalysis.
// Takes the project id ONLY: no document id, no user id, no scope -- the
// per-reader filter is entirely server-side from the verified token.
export const getProjectClaims = (projectId: string): Promise<ProjectClaimsResult> =>
  api
    .get<{ claims: ProjectClaim[]; document_names?: Record<string, string>; document_count?: number }>(
      `/novelty/project/${projectId}/claims`,
    )
    // Bug #5835: document_names is optional on the wire so a portal built ahead
    // of the Lambda deploy degrades to the previous behaviour rather than
    // throwing — it never fabricates a name. Story #5648 build v2's
    // document_count is optional for the same reason: a portal ahead of the
    // Lambda shows the claims without the denominator rather than a wrong one.
    .then((r) => ({
      claims: r.data.claims,
      documentNames: r.data.document_names ?? {},
      documentCount: r.data.document_count ?? 0,
    }))

// POST /innovateflow/novelty/project/{projectId}/clarify → submit clarifying answers
export const submitProjectClarifyingAnswers = (projectId: string, answers: ClarifyingQuestion[]): Promise<ProjectAIAnalysis> =>
  api.post<{ ai_analysis: ProjectAIAnalysis }>(`/novelty/project/${projectId}/clarify`, { answers }).then((r) => r.data.ai_analysis)

// GET /innovateflow/novelty/project/{projectId}/prior-art → get project prior art summary
export const getProjectPriorArt = (projectId: string): Promise<PriorArtSummary> =>
  api.get<PriorArtSummary>(`/novelty/project/${projectId}/prior-art`).then((r) => r.data)

// POST /innovateflow/novelty/project/{projectId}/re-evaluate → re-evaluate prior art
export const reEvaluateProjectPriorArt = (projectId: string): Promise<ReprocessJob> =>
  api.post<ReprocessJob>(`/novelty/project/${projectId}/re-evaluate`).then((r) => r.data)

// POST /innovateflow/reprocess/{projectId} → reprocess entire project
export const reprocessProject = (projectId: string): Promise<ReprocessJob> =>
  api.post<ReprocessJob>(`/reprocess/${projectId}`).then((r) => r.data)

// GET /innovateflow/novelty/project/{projectId}/re-evaluate/{jobId} → get reprocess job status
export const getReprocessStatus = (projectId: string, jobId: string): Promise<ReprocessJob> =>
  api.get<ReprocessJob>(`/novelty/project/${projectId}/re-evaluate/${jobId}`).then((r) => r.data)

// GET /innovateflow/novelty/project/{projectId}/bom → auto-generated BOM from standard components
export interface BomEntry {
  document_id: string
  part_name: string
  standard_designation: string
  component_category: string
  confidence: number
  source: string
}
export const getProjectBom = (projectId: string): Promise<{ bom: BomEntry[]; total_parts: number }> =>
  api.get<{ bom: BomEntry[]; total_parts: number }>(`/novelty/project/${projectId}/bom`).then((r) => r.data)

// GET /innovateflow/novelty/folder/{projectId}/{folderId} → get folder AI analysis
export const getFolderAnalysis = (projectId: string, folderId: string): Promise<FolderAIAnalysis | null> =>
  api.get<{ ai_analysis: FolderAIAnalysis | null }>(`/novelty/folder/${projectId}/${folderId}`).then((r) => r.data.ai_analysis)

// POST /innovateflow/novelty/folder/{projectId}/{folderId} → request folder AI analysis
export const requestFolderAnalysis = (projectId: string, folderId: string): Promise<FolderAIAnalysis> =>
  api.post<{ ai_analysis: FolderAIAnalysis }>(`/novelty/folder/${projectId}/${folderId}`).then((r) => r.data.ai_analysis)

// PATCH /innovateflow/novelty/folder/{projectId}/{folderId} → update folder AI analysis with corrections
export const updateFolderAnalysis = (projectId: string, folderId: string, corrections: Partial<FolderAIAnalysis>): Promise<FolderAIAnalysis> =>
  api.patch<{ ai_analysis: FolderAIAnalysis }>(`/novelty/folder/${projectId}/${folderId}`, corrections).then((r) => r.data.ai_analysis)

// GET /innovateflow/novelty/folder/{projectId}/{folderId}/prior-art → get folder prior art summary
export const getFolderPriorArt = (projectId: string, folderId: string): Promise<PriorArtSummary> =>
  api.get<PriorArtSummary>(`/novelty/folder/${projectId}/${folderId}/prior-art`).then((r) => r.data)

// POST /innovateflow/novelty/folder/{projectId}/{folderId}/re-evaluate → re-evaluate folder prior art
export const reEvaluateFolderPriorArt = (projectId: string, folderId: string): Promise<ReprocessJob> =>
  api.post<ReprocessJob>(`/novelty/folder/${projectId}/${folderId}/re-evaluate`).then((r) => r.data)

// ---------------------------------------------------------------------------
// Signup — signup/handler.py
// ---------------------------------------------------------------------------

export interface SignupRequest {
  tier: 'trial' | 'basic' | 'inventor' | 'pro_inventor'
  email: string
  // BUG-096: the chosen password. Without it the backend can never create a
  // Cognito user for the account, so the person could never sign in. The backend
  // register() handler must read body['password'] and create the Cognito user.
  password: string
  // Optional profile fields collected on the redesigned sign-up form (Fig41).
  // The backend stores them as Cognito attributes where supported and ignores
  // any it does not recognise, so sending them is always safe.
  name?: string
  organisation?: string
  country?: string
}

export interface SignupResponse {
  tier: string
  subscription_status: string
  trial_expires_at?: string
  monthly_credit_usd?: number
  message?: string
  error?: string
}

export interface CheckoutSessionResponse {
  checkout_url: string
  session_id: string
}

export interface SubscriptionStatus {
  tier: string
  subscription_status: string
  remaining_usd?: string
  monthly_credit_usd?: string
  trial_expires_at?: string
  aggregator_id?: string
  entity_count?: number
}

export interface AggregatorStatus {
  aggregator_id: string
  entity_count: number
  tier: string
}

export interface UpdateSeatsRequest {
  aggregator_id: string
  entity_id: string
  delta: number
}

export interface UpdateSeatsResponse {
  aggregator_id: string
  entity_id: string
  delta: number
  previous_count: number
  entity_count: number
  tier: string
}

// POST /innovateflow/signup/register → register new user with tier
export const signupRegister = (data: SignupRequest): Promise<SignupResponse> =>
  api.post<SignupResponse>('/signup/register', data).then((r) => r.data)

// POST /innovateflow/signup/create-checkout-session → create Stripe checkout for paid tier
export const createCheckoutSession = (data: { tier: string; email: string }): Promise<CheckoutSessionResponse> =>
  api.post<CheckoutSessionResponse>('/signup/create-checkout-session', data).then((r) => r.data)

// GET /innovateflow/signup/subscription-status → get current subscription
export const getSubscriptionStatus = (): Promise<SubscriptionStatus> =>
  api.get<SubscriptionStatus>('/signup/subscription-status').then((r) => r.data)

// POST /innovateflow/signup/customer-portal → get Stripe customer portal URL
export const getCustomerPortal = (): Promise<{ portal_url: string }> =>
  api.post<{ portal_url: string }>('/signup/customer-portal').then((r) => r.data)

// POST /innovateflow/signup/upgrade → upgrade subscription tier
export const upgradeSubscription = (data: { new_tier: string }): Promise<SignupResponse> =>
  api.post<SignupResponse>('/signup/upgrade', data).then((r) => r.data)

// POST /innovateflow/signup/aggregator/seats → add or remove entity seats
export const updateAggregatorSeats = (data: UpdateSeatsRequest): Promise<UpdateSeatsResponse> =>
  api.post<UpdateSeatsResponse>('/signup/aggregator/seats', data).then((r) => r.data)

// ---------------------------------------------------------------------------
// Billing — billing/handler.py
// ---------------------------------------------------------------------------

export interface BalanceInfo {
  tier: string
  subscription_status: string
  monthly_credit_usd: string
  used_usd: string
  remaining_usd: string
}

export interface UsageRecord {
  timestamp: string
  scope: string
  model: string
  tokens: number
  cost_usd: number
}

// GET /innovateflow/billing/balance → get current balance
export const getBillingBalance = (): Promise<BalanceInfo> =>
  api.get<BalanceInfo>('/billing/balance').then((r) => r.data)

// GET /innovateflow/billing/usage → get usage history
export const getBillingUsage = (): Promise<{ items: UsageRecord[] }> =>
  api.get<{ items: UsageRecord[] }>('/billing/usage').then((r) => r.data)

// --- Users / Org Switcher ---

// GET /innovateflow/users/me → current user profile + org memberships
export const getMe = (): Promise<{ user: { user_id: string; email: string; role: string; groups: string[]; org_memberships: OrgMembership[] } }> =>
  api.get('/users/me').then((r) => r.data)

// PATCH /innovateflow/users/me → set active org
export const patchActiveOrg = (active_org_id: string): Promise<{ active_org_id: string }> =>
  api.patch<{ active_org_id: string }>('/users/me', { active_org_id }).then((r) => r.data)

// ---------------------------------------------------------------------------
// MFA (multi-factor authentication) — users/handler.py MFA endpoints + the
// unauthenticated auth/ recovery tree. See
// documentation/core/InnovateFlow_MFA_Implementation_Plan.md.
//
// D14 two-token transport: the axios interceptor already sends the ID token as
// `Authorization: Bearer` (validated by the API-GW authorizer); these MFA state-
// changing endpoints additionally require the ACCESS token in the request BODY
// so the server can run its confused-deputy guard (verify access-token sub ==
// authorizer sub) and notify synchronously. We read the access token from the
// same localStorage slot the browser-direct ChangePassword uses (Settings.tsx).
// ---------------------------------------------------------------------------

const readAccessToken = (): string => localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) || ''

// Cognito method tokens — the exact strings SetUserMFAPreference / the challenge
// flow use. Kept as a string-literal union here so this module is self-contained
// (does not depend on the Phase-1 enums additions owned by a sibling agent).
export type MfaMethodToken = 'SOFTWARE_TOKEN_MFA' | 'SMS_MFA' | 'EMAIL_OTP'

export interface MfaMethodInfo {
  type: MfaMethodToken
  added_at?: string
}
export interface MfaRecoveryInfo {
  remaining: number
  generated_at?: string
}
export interface MfaStatusInfo {
  preferred: MfaMethodToken | null
  methods: MfaMethodInfo[]
  recovery: MfaRecoveryInfo
}
export interface MyMfaProfile {
  mfa?: MfaStatusInfo
  /** true ONLY for genuinely password-less federation-only accounts
   *  (AdminGetUser.UserStatus == 'EXTERNAL_PROVIDER'); drives mockup screen 6. */
  mfa_managed_externally?: boolean
  /** org attestation from org-identity-config — softens/attests evidence copy. */
  idp_enforces_mfa?: boolean
}

// GET /innovateflow/users/me → existing shape PLUS the mfa block (D7/D9). The
// sibling backend may return the mfa fields at the response top level or nested
// under `user`; read both defensively so a wire-shape choice can't break us.
interface MyMfaProfileRaw {
  mfa?: MfaStatusInfo
  mfa_managed_externally?: boolean
  idp_enforces_mfa?: boolean
  user?: { mfa?: MfaStatusInfo; mfa_managed_externally?: boolean; idp_enforces_mfa?: boolean }
}
export const getMyMfaStatus = (): Promise<MyMfaProfile> =>
  api.get<MyMfaProfileRaw>('/users/me').then((r) => {
    const d = r.data
    return {
      mfa: d.mfa ?? d.user?.mfa,
      mfa_managed_externally: d.mfa_managed_externally ?? d.user?.mfa_managed_externally,
      idp_enforces_mfa: d.idp_enforces_mfa ?? d.user?.idp_enforces_mfa,
    }
  })

// POST /innovateflow/users/me/mfa/associate → begin TOTP enrollment (D12).
export interface MfaAssociateResponse { secretCode: string; otpauthUri: string }
export const associateMfa = (): Promise<MfaAssociateResponse> =>
  api.post<MfaAssociateResponse>('/users/me/mfa/associate', { access_token: readAccessToken() }).then((r) => r.data)

// POST /innovateflow/users/me/mfa/verify → confirm the TOTP pairing (writes added_at).
export const verifyMfa = (code: string, friendlyDeviceName?: string): Promise<{ status: string }> =>
  api.post<{ status: string }>('/users/me/mfa/verify', {
    access_token: readAccessToken(),
    code,
    ...(friendlyDeviceName ? { friendlyDeviceName } : {}),
  }).then((r) => r.data)

// POST /innovateflow/users/me/mfa/set-preference → make `method` the preferred
// (challengeable) factor. Server enforces the D6 "always ≥1 preferred" invariant.
export const setMfaPreference = (method: MfaMethodToken): Promise<{ preferred: MfaMethodToken }> =>
  api.post<{ preferred: MfaMethodToken }>('/users/me/mfa/set-preference', {
    access_token: readAccessToken(),
    method,
  }).then((r) => r.data)

// POST /innovateflow/users/me/mfa/disable → turn MFA off (opt-out, D8). The
// client re-auth (password + current code) that precedes this is the UX gate;
// the server owns the authoritative disable + notification.
export const disableMfa = (): Promise<{ status: string }> =>
  api.post<{ status: string }>('/users/me/mfa/disable', { access_token: readAccessToken() }).then((r) => r.data)

// POST /innovateflow/users/me/attributes → change email/phone with genuine
// re-auth (D14/R19/R20). `mfaCode` is required for MFA-enrolled users.
export const changeMfaAttribute = (
  attribute: 'email' | 'phone_number',
  value: string,
  password: string,
  mfaCode?: string,
): Promise<{ status: 'pending-verification' | 'changed' }> =>
  api.post<{ status: 'pending-verification' | 'changed' }>('/users/me/attributes', {
    access_token: readAccessToken(),
    attribute,
    value,
    password,
    ...(mfaCode ? { mfaCode } : {}),
  }).then((r) => r.data)

// GET /innovateflow/users/me/recovery-codes → { remaining, generated_at } (D4).
export const getRecoveryCodesStatus = (): Promise<MfaRecoveryInfo> =>
  api.get<MfaRecoveryInfo>('/users/me/recovery-codes').then((r) => r.data)

// POST /innovateflow/users/me/recovery-codes → regenerate; codes returned ONCE.
export interface RecoveryCodesGenerated { codes: string[]; generated_at?: string }
export const regenerateRecoveryCodes = (): Promise<RecoveryCodesGenerated> =>
  api.post<RecoveryCodesGenerated>('/users/me/recovery-codes', {}).then((r) => r.data)

// POST /innovateflow/auth/recovery → UNAUTHENTICATED step 1 of the two-step
// break-glass (D15). Never mints tokens; emails a confirmation link.
export const requestRecovery = (email: string, code: string): Promise<{ ok: string }> =>
  api.post<{ ok: string }>('/auth/recovery', { email, code }).then((r) => r.data)

// POST /innovateflow/auth/recovery/confirm → UNAUTHENTICATED step 2 (D15). The
// SPA landing reads the emailed `?token=` (a GET) and issues this POST.
export const confirmRecovery = (token: string): Promise<{ status: string }> =>
  api.post<{ status: string }>('/auth/recovery/confirm', { token }).then((r) => r.data)

// --- Aggregator Portfolio (W4) ---
import type {
  AggregatorPortfolioData, AggregatorEntity, AggregatorGrant, AggregatorTeamMember,
  AggregatorMembership, EmbeddingView, AggregatorRole, OwnScopePortfolioProjectsData,
} from '../types'

// GET /innovateflow/aggregator/my-memberships → all aggregator memberships for caller
export const getMyAggregatorMemberships = (): Promise<{ memberships: AggregatorMembership[]; count: number }> =>
  api.get<{ memberships: AggregatorMembership[]; count: number }>('/aggregator/my-memberships').then((r) => r.data)

// GET /innovateflow/aggregator/portfolio-projects → list granted projects (aggregator view)
// aggregatorId sent as X-Aggregator-Id header for multi-aggregator scoping
export const getAggregatorPortfolioProjects = (aggregatorId?: string | null): Promise<AggregatorPortfolioData> =>
  api.get<AggregatorPortfolioData>('/aggregator/portfolio-projects', {
    headers: aggregatorId ? { 'X-Aggregator-Id': aggregatorId } : undefined,
  }).then((r) => r.data)

// GET /innovateflow/aggregator/portfolio-projects?scope=own → Story #5648
// Task #5736. A SEPARATE exported fetcher, not a conditional-return-type
// overload of getAggregatorPortfolioProjects above: A8/A6 read this at own
// scope, and a union return type would force every one of their call sites
// (files A7 does not own) to narrow the type before use. The response shape
// (OwnScopePortfolioProjectsData) is A3's enumerated six-field allow-list —
// see the type's own comment for why it cannot be AggregatorPortfolioData
// with fields made optional.
export const getOwnScopePortfolioProjects = (): Promise<OwnScopePortfolioProjectsData> =>
  api
    .get<OwnScopePortfolioProjectsData>('/aggregator/portfolio-projects', scopedRequest('own', null))
    .then((r) => r.data)

// GET /innovateflow/aggregator/across → Story #5648 Task #5740, A9's S7.
// The set-union of the caller's real portfolio across EVERY aggregator
// membership they hold (B4's aggregator/across.py, dispatched by A3's
// aggregator/handler.py sub=='across'). aggregatorId is REQUIRED, but it is
// NOT a scoping parameter -- verified live 4 Sep 2026 (sarah-acme, 2 real
// memberships): calling with either of her two aggregator ids returns a
// byte-identical response (same own_org_project_ids count, same aggregators
// array covering BOTH her memberships). The shared aggregator/handler.py
// dispatcher gates every sub-route on get_aggregator_context resolving
// SOME membership before the route body runs at all -- across.py itself
// then ignores that value and recomputes the union from every membership
// independently. Calling with no header at all is a 403 (get_aggregator_context
// cannot resolve one of several memberships without a hint), which is why
// this fetcher cannot omit the argument the way the design doc originally
// described ("no header, no scope") -- the real deployed gate still wants one.
export const getAcrossPortfolios = (aggregatorId: string): Promise<AcrossPortfoliosResponse> =>
  api
    .get<AcrossPortfoliosResponse>('/aggregator/across', { headers: { 'X-Aggregator-Id': aggregatorId } })
    .then((r) => r.data)

// GET /innovateflow/aggregator/entities → role-masked entity list
// entity_cap / entity_count (Story #5648 bug #5841): the aggregator's real
// per-tier entity cap and its full roster size, used by Figure 7's seat-panel
// footer. entity_cap is null on an unlimited plan — the footer prints no cap
// rather than a number that does not exist.
export const getAggregatorEntities = (aggregatorId?: string | null): Promise<{ entities: AggregatorEntity[]; count: number; entity_cap?: number | null; entity_count?: number }> =>
  api.get<{ entities: AggregatorEntity[]; count: number; entity_cap?: number | null; entity_count?: number }>('/aggregator/entities', {
    headers: aggregatorId ? { 'X-Aggregator-Id': aggregatorId } : undefined,
  }).then((r) => r.data)

// GET /innovateflow/aggregator/entities/{entityId}/embedding-view → sponsor-only
export const getEmbeddingView = (entityId: string, aggregatorId?: string | null): Promise<EmbeddingView> =>
  api.get<EmbeddingView>(`/aggregator/entities/${entityId}/embedding-view`, {
    headers: aggregatorId ? { 'X-Aggregator-Id': aggregatorId } : undefined,
  }).then((r) => r.data)

// GET /innovateflow/aggregator-grants/projects/{projectId}/grants
export const listAggregatorGrants = (projectId: string): Promise<{ grants: AggregatorGrant[] }> =>
  api.get<{ grants: AggregatorGrant[] }>(`/aggregator-grants/projects/${projectId}/grants`).then((r) => r.data)

// POST /innovateflow/aggregator-grants/projects/{projectId}/grants
export const createAggregatorGrant = (projectId: string, aggregatorId: string, expiresAt?: string): Promise<AggregatorGrant> =>
  api.post<AggregatorGrant>(`/aggregator-grants/projects/${projectId}/grants`, { aggregator_id: aggregatorId, expires_at: expiresAt }).then((r) => r.data)

// DELETE /innovateflow/aggregator-grants/projects/{projectId}/grants/{aggregatorId}
export const revokeAggregatorGrant = (projectId: string, aggregatorId: string): Promise<void> =>
  api.delete(`/aggregator-grants/projects/${projectId}/grants/${aggregatorId}`).then(() => undefined)

// POST /innovateflow/aggregator-grants/projects/{projectId}/grants/{aggregatorId}/exclusions
export const addAggregatorFolderExclusion = (projectId: string, aggregatorId: string, folderIds: string[], reason?: string): Promise<void> =>
  api.post(`/aggregator-grants/projects/${projectId}/grants/${aggregatorId}/exclusions`, { folder_ids: folderIds, reason }).then(() => undefined)

// DELETE /innovateflow/aggregator-grants/projects/{projectId}/grants/{aggregatorId}/exclusions/{folderId}
export const removeAggregatorFolderExclusion = (projectId: string, aggregatorId: string, folderId: string): Promise<void> =>
  api.delete(`/aggregator-grants/projects/${projectId}/grants/${aggregatorId}/exclusions/${folderId}`).then(() => undefined)

// GET /innovateflow/aggregator-grants/projects/{projectId}/available-aggregators
export const listAvailableAggregators = (projectId: string): Promise<{ aggregator_id: string; aggregator_name: string }[]> =>
  api.get<{ aggregators: { aggregator_id: string; aggregator_name: string }[] }>(`/aggregator-grants/projects/${projectId}/available-aggregators`)
    .then(r => r.data.aggregators ?? [])

// GET /innovateflow/aggregator-team
export const listAggregatorTeamMembers = (): Promise<{ members: AggregatorTeamMember[]; caller_role: AggregatorRole | null; aggregator_name: string | null }> =>
  // Backend returns { team, caller_role, aggregator_name }; expose team as
  // `members`, surface caller_role so the UI gates actions on the caller's role,
  // and surface aggregator_name so the page title shows the company name rather
  // than the raw aggregator UUID (T6).
  api.get<{ team?: AggregatorTeamMember[]; caller_role?: AggregatorRole; aggregator_name?: string }>('/aggregator-team')
    .then((r) => ({ members: r.data.team ?? [], caller_role: r.data.caller_role ?? null, aggregator_name: r.data.aggregator_name ?? null }))

// POST /innovateflow/aggregator-team/invite
export const inviteAggregatorTeamMember = (email: string, role: AggregatorRole): Promise<{ invite_link: string }> =>
  api.post<{ invite_link: string }>('/aggregator-team/invite', { email, role }).then((r) => r.data)

// DELETE /innovateflow/aggregator-team/{memberUserId}
export const removeAggregatorTeamMember = (memberUserId: string): Promise<void> =>
  api.delete(`/aggregator-team/${memberUserId}`).then(() => undefined)

// PUT /innovateflow/aggregator-team/{memberUserId}/role
export const changeAggregatorTeamMemberRole = (memberUserId: string, role: AggregatorRole): Promise<void> =>
  api.put(`/aggregator-team/${memberUserId}/role`, { role }).then(() => undefined)

// POST /innovateflow/aggregator-team/transfer-billing-owner
export const transferAggregatorBillingOwner = (newBillingOwnerUserId: string): Promise<void> =>
  api.post('/aggregator-team/transfer-billing-owner', { new_billing_owner_user_id: newBillingOwnerUserId }).then(() => undefined)

// GET /innovateflow/aggregator-team/{analystUserId}/project-visibility
// Returns the analyst's CURRENT hidden project ids so the editor can seed its
// state and diff a Save against the real stored set (BUG-017). Without this the
// editor starts with an empty hidden set and any Save PUTs an empty list, which
// the backend treats as "un-hide everything".
// The GET route shipped: aggregator_team/handler.py:700 (dispatch :786-788).
export const getAnalystProjectVisibility = (analystUserId: string): Promise<{ hidden_project_ids: string[] }> =>
  api.get<{ hidden_project_ids?: string[] }>(`/aggregator-team/${analystUserId}/project-visibility`)
    .then((r) => ({ hidden_project_ids: r.data.hidden_project_ids ?? [] }))

// PUT /innovateflow/aggregator-team/{analystUserId}/project-visibility
export const setAnalystProjectVisibility = (analystUserId: string, hiddenProjectIds: string[]): Promise<void> =>
  api.put(`/aggregator-team/${analystUserId}/project-visibility`, { hidden_project_ids: hiddenProjectIds }).then(() => undefined)

// PUT /innovateflow/sponsorship/extended-visibility/{entityId}
// Bug #5829: aggregatorId names WHICH sponsor's row to change. It is optional
// because the sponsor-side callers (AggregatorSharingPanel, EntityGroupView)
// carry their own aggregator context server-side and never needed it — but for
// an INVENTOR with more than one active sponsor the route now refuses to guess,
// answering 400 with the candidate ids rather than changing whichever row
// DynamoDB returned first. Omitting it stays correct for a single-sponsor
// inventor, which is what every existing caller is.
export const setExtendedVisibility = (
  entityId: string,
  enabled: boolean,
  confirmation: string,
  aggregatorId?: string,
): Promise<void> =>
  api
    .put(`/sponsorship/extended-visibility/${entityId}`, {
      extended_visibility: enabled,
      confirmation,
      ...(aggregatorId ? { aggregator_id: aggregatorId } : {}),
    })
    .then(() => undefined)

// POST /innovateflow/sponsorship/end/{entityId} — the C1 drawer's "End
// relationship". The route keeps its existing name; what the screen calls it
// is the sharing word, because ending it is about sharing, not about paying.
export const endSponsorship = (entityId: string): Promise<void> =>
  api.post(`/sponsorship/end/${entityId}`, {}).then(() => undefined)

// POST /innovateflow/aggregator/entities/{entityId}/ask-to-share — figure S5's
// "Ask the owners to share more" (Story #5648 build v2). A notification and
// nothing else: it grants no access, names no project, and never says how many
// more. One ask per entity every seven days; a second inside the window is a
// 429 the caller shows as a plain sentence, never a retry loop.
export const askEntityToShareMore = (
  entityId: string,
  aggregatorId?: string | null,
): Promise<{ asked_at: string; next_ask_allowed_after_days: number }> =>
  api
    .post<{ asked_at: string; next_ask_allowed_after_days: number }>(
      `/aggregator/entities/${entityId}/ask-to-share`,
      {},
      { headers: aggregatorId ? { 'X-Aggregator-Id': aggregatorId } : undefined },
    )
    .then((r) => r.data)

// POST /innovateflow/sponsorship/suspend/{entityId} — Bug #5858 (Figure 13's
// Suspend seat): pause a sponsored seat without ending it. billing_owner only;
// 409 once ended or already suspended.
export const suspendSponsorship = (entityId: string): Promise<{ status: string; sponsor_suspended_at?: string }> =>
  api.post<{ status: string; sponsor_suspended_at?: string }>(`/sponsorship/suspend/${entityId}`, {}).then((r) => r.data)

// POST /innovateflow/sponsorship/resume/{entityId} — the inverse; 409 unless suspended.
export const resumeSponsorship = (entityId: string): Promise<{ status: string }> =>
  api.post<{ status: string }>(`/sponsorship/resume/${entityId}`, {}).then((r) => r.data)

// GET /innovateflow/sponsorship/pending — Story #5648 Task #5741 (B5's
// listPendingSeats). Takes NO arguments -- not a user id, not an entity id,
// not an email. The caller is the token (the verified caller identity);
// a parameter here would be an enumeration oracle, per B2's criterion.
export const listPendingSeats = (): Promise<PendingSeat[]> =>
  api.get<{ pending: PendingSeat[]; count: number }>('/sponsorship/pending').then((r) => r.data.pending)

// POST /innovateflow/sponsorship/accept — Story #5648 Task #5741.
// DEVIATES from this task's own draft signature (acceptSponsorship(token)):
// the real deployed route (sponsorship/handler.py:_handle_accept) never
// mints, stores, compares or returns a token at all -- SES is unavailable
// in this AWS account (measured live: sesv2/ses list-identities both
// empty), so B2 chose identity-only accept rather than a link the sponsor
// would have to copy (which rebuilds the self-accept primitive the token
// rule exists to prevent). The caller's entity_id is derived from the
// verified token server-side; the body carries only aggregator_id, which
// is a resource identifier naming WHICH offer to accept, never an
// identity. Do not add an entityId parameter "for symmetry" -- that is
// exactly the self-accept primitive B2's criterion bans.
export const acceptSponsorship = (aggregatorId: string): Promise<AcceptSponsorshipResponse> =>
  api
    .post<AcceptSponsorshipResponse>('/sponsorship/accept', { aggregator_id: aggregatorId })
    .then((r) => r.data)

// POST /innovateflow/sponsorship/decline — Story #5648 build v2, screen C2.
// The mirror of acceptSponsorship and it follows the same rules: the caller is
// the token, and the body carries only aggregator_id, which names WHICH
// invitation, never who the caller is. Declining closes the offer and nothing
// else — a pending invitation grants nothing, so there is nothing to revoke, and
// the project grants the inventor made themselves are deliberately untouched.
export const declinePortfolioInvitation = (aggregatorId: string): Promise<void> =>
  api.post('/sponsorship/decline', { aggregator_id: aggregatorId }).then(() => undefined)

// POST /innovateflow/sponsorship/leave — Story #5648 build v2, screen C2.
// The inventor's own half of ending a relationship (/sponsorship/end is the
// organisation's half). It ends their structural place in that portfolio and
// turns extended visibility off; it leaves EVERY grant exactly as it was,
// which is what the screen promises beside the button.
export const leavePortfolio = (aggregatorId: string): Promise<void> =>
  api.post('/sponsorship/leave', { aggregator_id: aggregatorId }).then(() => undefined)

// GET /innovateflow/sponsorship/seat-changes — Story #5648 Task #5781 (B5's
// event feed). No X-Aggregator-Id, no entity id argument -- caller-scoped
// entirely from the verified token, matching listPendingSeats' shape above.
export const listSeatChanges = (): Promise<SeatChange[]> =>
  api.get<{ changes: SeatChange[]; count: number }>('/sponsorship/seat-changes').then((r) => r.data.changes)

// GET /innovateflow/sponsorship/my-seats — Story #5648 C2, Task #5828 (the read
// path SeatSummary and ExtendedVisibilityRow had none of). Takes NO arguments,
// exactly like listPendingSeats and listSeatChanges above: the caller is the
// token. An entityId parameter here would be an enumeration oracle for who is
// sponsored by whom, which is the same rule B2 wrote for /pending.
//
// Returns EVERY active seat the caller holds, sponsored and self_paid alike.
// The sponsored/self_paid distinction is a rendering decision the components
// make (a self_paid seat under an organisation is a membership, not a
// sponsorship — and the deployed extended-visibility route agrees, refusing a
// self_paid-only inventor with 422), so it is deliberately NOT filtered here.
export const listMySponsors = (): Promise<MySeat[]> =>
  api.get<{ seats: MySeat[]; count: number }>('/sponsorship/my-seats').then((r) => r.data.seats)

import type { SynergyAnalysis, PendingSeat, AcceptSponsorshipResponse, SeatChange, MySeat } from '../types'

// ---------------------------------------------------------------------------
// User Preferences — users/preferences.py
// ---------------------------------------------------------------------------

// GET /innovateflow/users/preferences?key=... → get one preference
// 404 propagates as rejected promise — callers handle fallback (e.g. useAggregatorPreferences)
export const getUserPreference = (key: string): Promise<{ pref_key: string; value: unknown }> =>
  api.get('/users/preferences', { params: { key } }).then((r) => r.data)

// PUT /innovateflow/users/preferences → set one preference
export const setUserPreference = (key: string, value: unknown): Promise<void> =>
  api.put('/users/preferences', { pref_key: key, value }).then(() => undefined)

// POST /innovateflow/aggregator/bulk-discoverable → { updated: int, failed: [str] }
export const bulkSetDiscoverable = (
  projectIds: string[],
  discoverable: boolean,
  aggregatorId: string,
): Promise<{ updated: number; failed: string[] }> =>
  api.post(
    '/aggregator/bulk-discoverable',
    { project_ids: projectIds, discoverable },
    { headers: { 'X-Aggregator-Id': aggregatorId } },
  ).then((r) => r.data)

// POST /innovateflow/sponsorship/invite → entity-level sponsorship invite
export const inviteEntity = (
  entityId: string,
  email: string,
  tier: string,
  aggregatorId: string,
): Promise<void> =>
  api.post(
    '/sponsorship/invite',
    { entity_id: entityId, email, tier },
    { headers: { 'X-Aggregator-Id': aggregatorId } },
  ).then(() => undefined)

// GET /innovateflow/crosspollination/synergy-pair?project_a=...&project_b=...
export const getSynergyPair = (
  projectAId: string,
  projectBId: string,
  forceRefresh = false,
): Promise<{ status: 'available' | 'not_available'; analysis?: SynergyAnalysis }> =>
  api
    .get<{ status: 'available' | 'not_available'; analysis?: SynergyAnalysis }>(
      `/crosspollination/synergy-pair`,
      { params: { project_a: projectAId, project_b: projectBId, force_refresh: forceRefresh } },
    )
    .then((r) => r.data)

// ---------------------------------------------------------------------------
// IP arrangements — ip_arrangements/handler.py (Story #5648 build v2, C2/C1/S5)
//
// An arrangement records that an option, a licence or an assignment was agreed
// OUTSIDE InnovateFlow. Not one of these calls changes what anybody can see:
// the record is a record, and the sharing grid is the only thing that decides
// visibility. Acknowledging is therefore safe to offer without a warning about
// access — the confirmation the screen shows is about the LEGAL meaning, not
// about permissions.
// ---------------------------------------------------------------------------

// GET /innovateflow/ip-arrangements/mine — the caller's own arrangements.
// Takes NO argument, exactly like listPendingSeats and listMySponsors: the
// caller is the verified token. A user id here would let anyone ask what has
// been proposed on somebody else's inventions.
export const listMyIpArrangements = (): Promise<IpArrangement[]> =>
  api.get<{ arrangements: IpArrangement[]; count: number }>('/ip-arrangements/mine')
    .then((r) => r.data.arrangements ?? [])

// POST /innovateflow/ip-arrangements/{arrangementId}/{acknowledge|decline|dispute}
// A project owner only; the API decides that from the token, never from a body.
export const acknowledgeIpArrangement = (arrangementId: string): Promise<IpArrangement> =>
  api.post<IpArrangement>(`/ip-arrangements/${arrangementId}/acknowledge`, {}).then((r) => r.data)

export const declineIpArrangement = (arrangementId: string, note?: string): Promise<IpArrangement> =>
  api.post<IpArrangement>(`/ip-arrangements/${arrangementId}/decline`, note ? { note } : {})
    .then((r) => r.data)

export const disputeIpArrangement = (arrangementId: string, note?: string): Promise<IpArrangement> =>
  api.post<IpArrangement>(`/ip-arrangements/${arrangementId}/dispute`, note ? { note } : {})
    .then((r) => r.data)

// GET /innovateflow/ip-arrangements/{aggregatorId}/{entityId} — the holder's
// side, for the C1 drawer and the S5 entity header. Counts only: this response
// carries no owner id, name or email at all.
export const listEntityIpArrangements = (
  aggregatorId: string,
  entityId: string,
): Promise<IpArrangementHolderRow[]> =>
  api.get<{ arrangements: IpArrangementHolderRow[]; count: number }>(
    `/ip-arrangements/${aggregatorId}/${entityId}`,
  ).then((r) => r.data.arrangements ?? [])

// POST /innovateflow/ip-arrangements/{aggregatorId}/{entityId} — propose.
// Billing Owner or Admin of that organisation; an Analyst is refused (403), and
// so is a project the inventor has not shared with this portfolio.
export const proposeIpArrangement = (
  aggregatorId: string,
  entityId: string,
  proposal: IpArrangementProposal,
): Promise<IpArrangementHolderRow> =>
  api.post<IpArrangementHolderRow>(`/ip-arrangements/${aggregatorId}/${entityId}`, proposal)
    .then((r) => r.data)

import type {
  IpArrangement, IpArrangementHolderRow, IpArrangementProposal,
} from '../types/ipArrangements'
